/**
 * Client Dashboard V3 - Luxury Modern Design
 * Updated: October 2025
 * Color Palette: Clean Minimalist (Place.com / Luxury Presence inspired)
 * Primary: Teal #59c4c4 | Neutral: Grays & Whites
 */
import React, { useState, useEffect, useRef } from 'react';
import ModalLayer from './ModalLayer';
import { useTheme } from '../ThemeContext';
import { reassignTasks } from '../utils/taskAssignment';
import { installPullRefresh } from '../utils/pullRefresh';
import { recordControlClick } from '../shared/tapCount';
import { dismissTasksReady, shouldShowTasksReady, tasksReadyDismissed } from '../shared/tasksReadyNotice';
import { canonicalPropertyType, featureSelected, propertyTypeChoices, toggleFeature } from '../shared/homeownerIntake';
import { dismissHomeFactsCard, dismissHomeProfilePrompt, homeFactsCardDismissed, homeFactsInviteDone, homeProfileIsBlank, homeProfilePromptDismissed, homeProfileSavePatch, homeSetupFinished, readHomeProfile } from '../shared/homeProfile';
import HomeProfileQuestions, { HomeFactsInviteCard, HomeProfilePromptCard } from './HomeProfileQuestions';
import HomeFactsFields from './HomeFactsFields';
import TaskPhotoThumb from './TaskPhotoThumb';
import MonthChecklist from './MonthChecklist';
import { VISIBLE_FEATURES, acFilterDescription, featuresFromFacts, homeFactsPatch, isAcFilterTask, plainTaskTitle } from '../shared/homeFacts';
import { homeownerPageFromPath, pathForHomeownerTab } from '../shared/homeownerRoutes';
import { projectChecklistCopy } from '../shared/projectChecklistCopy';
import { PUSH_OPEN_EVENT } from '../utils/pushNavigation';
import { APP_REFRESH_EVENT } from '../utils/appRefresh';
import { openExternalUrl } from '../utils/openExternal';
import { devLog } from '../utils/devLog';
import { buildSystemData, featuresAfterInventorySave, systemsMapFromRows, INVENTORY_TYPES } from '../shared/inventoryItem';
import { addTaskVendor, removeTaskVendor, taskVendorList } from '../shared/projectTaskVendor';
import { VENDOR_LIMIT_MESSAGE } from '../shared/projectTaskLimits';
import ProjectTaskMedia from './ProjectTaskMedia';
import HomeownerProjectForm from './HomeownerProjectForm';
import { deleteHomeownerTaskPhoto, uploadHomeownerTaskPhotos, warmTaskPhoto } from '../utils/projectTaskPhotoClient';
import { chatHistoryPayload } from '../shared/chatHistory';
import { latestApplianceLabel } from '../shared/applianceLabel';
import { messagesInContext, mergeOlder, rowsToMessages } from '../shared/assistantThread';
import { assistantErrorMessage, messagesBeforeSend, readAssistantJson } from '../shared/assistantTurn';
import { postAssistant } from '../shared/timedFetch';
import { noteAssistantFailure } from '../shared/clientErrorReport';
import { prepareAssistantPhoto } from '../utils/taskPhotoFile';
import AssistantThread from './AssistantThread';
import AnnouncementBanner from './AnnouncementBanner';
import { resetAppScroll } from '../utils/appScroll';
import { matchProjectTaskVendors, noTradeProsNote, tradeForTaskCategory, vendorMatchesTrade } from '../utils/vendorMatcher';
import { effortLabel, snoozeUntil, sponsorVendorForTask, taskSummaryText } from '../shared/taskHelp';
import TaskHelpCard from './TaskHelpCard';
import {
  ADDED_BY_YOU,
  ADD_VENDOR,
  EMPTY_LIST_VENDORS,
  SAVED_VETTING_LINE,
  SPONSOR_VENDORS_HELP,
  YOUR_VENDORS,
  YOUR_VENDORS_HELP,
  findAProResults,
  sponsorVendorHeading,
  withoutAdoptedCopies,
} from '../shared/homeownerPros';
import { vendorSoftCardStyle, vendorSoftShadow } from '../shared/vendorCardStyle';
import { stripVendorListingDisclaimer } from '../shared/vendorDescription';
import { HomeownerProCard, HomeownerProSheet, SavedProvidersSection } from './HomeownerPros';
import ClientTutorial from './ClientTutorial';
import HomeSearchBar from './HomeSearchBar';
import { buildHomeSearchCatalog } from '../shared/homeSearch';
import { HomeownerNotificationSettings, HOMEOWNER_PREF_DEFAULTS } from './NotificationPreferences';
import { HOMEOWNER_BOOL_FIELDS } from '../shared/notificationDelivery';
import { LEGAL_INDEX_PATH, followPolicyLink, openLegalPath } from '../utils/legal';
import { BeforeYouHireModal, useVendorHireGate } from './VendorConsent';
import { providerSuggestionLine } from '../shared/vendorConsent';
import PropertyContacts from './PropertyContacts';
import PropertyPhoto from './PropertyPhoto';
import usePropertyPhotoDisplay from '../shared/usePropertyPhotoDisplay';
import InventoryItemForm from './InventoryItemForm';
import YearlyCheckinCard from './YearlyCheckinCard';
import HomePhaseCards from './HomePhaseCards';
import { HomeownerAiNotice } from './AiAssistantNotice';
import GridLabel from './GridLabel';
import HomeownerDiscount from './HomeownerDiscount';
import TapToExpand, { VendorCategorySelect, VendorTapCard, callControlStyle } from './TapToExpand';
import { projectPhotoCount, projectSummaryLine } from '../shared/projectCardSummary';
import { CountyField, ServesLine } from './ServiceAreaFields';
import { countyLabel, servesCounty, sortVendorsByCounty } from '../shared/serviceArea';
import HomeownerPlanStep from './HomeownerPlanStep';
import ErrorBoundary from './ErrorBoundary';
import { homeownerRecords, homeownerTasks, homeownerPost, setHomeownerPassword, updateHomeownerClient, loadHomeownerClient, listHomeownerHomes, switchHomeownerHome, startHomeownerCheckout, loadHomeownerBilling } from '../utils/homeownerApi';
import InviteFriendPane from './InviteFriendPane';
import FriendStatus from './FriendStatus';
import { homeIsArchived } from '../shared/homeAccount';
import MoveHome, { ArchivedHomeBanner, SponsorCodeForm } from './MoveHome';
import AccountSignIn from './AccountSignIn';
import AppearanceChoice from './AppearanceChoice';
import { localDateString } from '../utils/recurringTasks';
import { formatDateOnly, parseLocalDate, tasksOnDay, isBeforeDateOnly, dateOnlyKey } from '../utils/dates';
import { applyTaskCompletion, mergeLoadedTasks, calendarTaskGroups, calendarDayIndicator, visibleTaskList } from '../utils/taskListState';
import { homeownerTourChoiceStored, homeownerTourStorageKey, readStoredFlag, shouldAutoStartHomeownerTour } from '../utils/tourPrompt';
import { mergeClientSystems } from '../shared/clientDetail';
import { countRowsSince, monthStartIso, monthlyQuestionLimit } from '../shared/aiQuota';
import QuestionsLeftNote from './QuestionsLeftNote';
import { formatPhoneDisplay, phoneTelHref } from '../shared/phoneFormat';
import { catalogTaskCopy, floridaTaskDescription } from '../shared/regionalTaskCopy';
import { displayTaskDescription } from '../shared/homeRegion';
import { HOMEOWNER_DESKTOP_TAB_IDS, HOMEOWNER_MORE_LINK_IDS, homeownerTabs } from '../shared/homeownerNav';
import { PACARION_BRAND_COLOR } from '../shared/sponsorlessBrand';
import HomeHistoryReportPage from './HomeHistoryReportPage';
import HomeHistoryRequestNotice from './HomeHistoryRequestNotice';
import HomePreviousOwnerCard from './HomePreviousOwnerCard';
import HomeownerPaywall, { HomeownerPlanBanner } from './HomeownerPaywall';
import PacarionHomeBrand from './PacarionHomeBrand';
import { getHomeownerToken } from '../utils/storage';
import confetti from 'canvas-confetti';
import { getYouTubeThumbnail, getYouTubeEmbedUrl } from '../utils/youtubeHelpers';
import { setAppBadge } from '../utils/pushNotifications';
import { HOMEOWNER_MOBILE_TABS, incompleteProjectTaskCount, projectTabBadgeCount, nextSeenProjectCount } from '../shared/mobileTabs';
import AppTabBar from './AppTabBar';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { getApiUrl, isNativeApp } from '../utils/platform';
import { HOME_REPORT_PLAN_MESSAGE, HOMEOWNER_PLAN_INACTIVE_MESSAGE } from '../shared/homeownerEntitlement';
import { contentTypeForFile, documentTitleForSave, documentTitleAfterFilePick, homeownerDocumentFile, storageErrorMessage } from '../utils/fileUpload';
import HomeownerDocumentsPanel from './HomeownerDocumentsPanel';
import { WRAP_TEXT } from '../shared/wrapText';
import { Browser } from '@capacitor/browser';
import { clearToasts, useToast } from './Toast';
import HomeownerAccount from './HomeownerAccount';
import {
  HVAC_TYPES,
  ROOF_TYPES,
  WATER_HEATER_TYPES,
  WATER_SOURCES,
  SEWER_TYPES,
  LOT_SIZES,
  getYearOptions,
} from '../utils/systemLifeExpectancy';

function scrollToDomId(id) {
  if (!id || typeof window === 'undefined') return;
  window.setTimeout(() => {
    const node = document.getElementById(id);
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'center' });
  }, 120);
}

function planLimitedMessage(error, fallback) {
  if (error && error.status === 402 && error.message) return error.message;
  return fallback;
}

function OverviewPropertyHero({ url, address, propertyType, isMobile }) {
  const photo = usePropertyPhotoDisplay(url);
  if (!url || photo.missing) return null;
  const typeLabel = propertyType
    ? propertyType.replace('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : '';
  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: isMobile ? '200px' : '250px',
      borderRadius: '12px',
      marginBottom: '2rem',
      display: 'flex',
      alignItems: 'flex-end',
      padding: '1.5rem',
      boxSizing: 'border-box',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      background: '#1f2933',
    }}>
      {photo.src ? (
        <img
          src={photo.src}
          alt=""
          onError={photo.onError}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : null}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3))',
      }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <h2 style={{
          margin: 0,
          color: 'white',
          fontSize: isMobile ? '1.3rem' : '1.5rem',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
        }}>
          {address || 'Your Property'}
        </h2>
        <p style={{
          margin: '0.25rem 0 0',
          color: 'rgba(255, 255, 255, 0.9)',
          fontSize: '0.9rem',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
        }}>
          {typeLabel}
        </p>
      </div>
    </div>
  );
}

const ClientDashboardV3 = ({ client, userId, onLogout, onTourFinished, onClientReplaced, initialTab, initialMorePane, invitePreview, previewProjects, previewAgent }) => {
  const preview = Array.isArray(previewProjects);
  const { colors, isDark } = useTheme();
  const { showToast } = useToast();

  const [showMove, setShowMove] = useState(false);
  const [morePane, setMorePane] = useState(initialMorePane || null);
  const [switchingHome, setSwitchingHome] = useState(false);
  const [accountHomes, setAccountHomes] = useState([]);
  const [canManagePlan, setCanManagePlan] = useState(false);
  const [homePlan, setHomePlan] = useState(null);
  const readOnlyHome = homeIsArchived(client);
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab) return initialTab;
    if (typeof window === 'undefined') return 'overview';
    const page = homeownerPageFromPath(window.location.pathname);
    if (page) return page;
    const section = new URLSearchParams(window.location.search).get('section');
    if (section === 'projects' || section === 'tasks' || section === 'overview' || section === 'assistant' || section === 'settings') return section;
    return 'overview';
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const seenTab = useRef(null);
  useEffect(() => {
    resetAppScroll();
    if (seenTab.current === activeTab) return;
    const first = seenTab.current === null;
    seenTab.current = activeTab;
    if (first) return;
    clearToasts();
    setPushUndo(null);
    setPushUndoTab(null);
    setMorePane(null);
  }, [activeTab]);

  const [tasks, setTasks] = useState([]);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [notifAudience, setNotifAudience] = useState('sponsorless');
  const [vendors, setVendors] = useState([]);
  const [homeownerPros, setHomeownerPros] = useState([]);
  const [proSheet, setProSheet] = useState(null);
  const [agent, setAgent] = useState(previewAgent || null);
  const [joinRequest, setJoinRequest] = useState(null);
  const [loading, setLoading] = useState(!preview);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [selectedVendorCategory, setSelectedVendorCategory] = useState('All');
  const [vendorTradeFilter, setVendorTradeFilter] = useState('');
  const [expandedVendorId, setExpandedVendorId] = useState(null);
  const [projects, setProjects] = useState(preview ? previewProjects : []);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [savingProject, setSavingProject] = useState(false);
  const [photoBusyKey, setPhotoBusyKey] = useState('');
  const [listRefreshing, setListRefreshing] = useState(false);
  const refreshHomeRef = useRef(() => {});
  const [incompleteTaskCount, setIncompleteTaskCount] = useState(0);
  const projectsSeenKey = client && client.id ? `pacarion_projects_badge_seen_${client.id}` : '';
  const [seenProjectCount, setSeenProjectCount] = useState(() => {
    if (!projectsSeenKey) return 0;
    try {
      const stored = Number(sessionStorage.getItem(projectsSeenKey));
      return Number.isFinite(stored) && stored > 0 ? stored : 0;
    } catch (error) {
      return 0;
    }
  });

  // Modals & UI state
  const [showPasswordSetup, setShowPasswordSetup] = useState(
    client.password_reset_required || (!client.has_password && !client.password_hash)
  );
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [editingProperty, setEditingProperty] = useState(false);
  const [editingTaskNote, setEditingTaskNote] = useState(null);
  const [taskNoteText, setTaskNoteText] = useState('');
  const [snoozeModal, setSnoozeModal] = useState({ show: false, taskId: null, date: '' });
  const [dismissModal, setDismissModal] = useState({ show: false, taskId: null, reason: '' });
  const [rescheduleModal, setRescheduleModal] = useState({ show: false, taskId: null, date: '' });
  const [reminderModal, setReminderModal] = useState({ show: false, taskId: null, date: '' });
  const [helpModal, setHelpModal] = useState({ show: false, task: null, message: '', sending: false });
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [pushTaskId, setPushTaskId] = useState(null);
  const [pushDate, setPushDate] = useState('');
  const [ownTaskEdit, setOwnTaskEdit] = useState(null);
  const [ownTaskDeleteId, setOwnTaskDeleteId] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushUndo, setPushUndo] = useState(null);
  const [pushUndoTab, setPushUndoTab] = useState(null);
  useEffect(() => {
    if (!pushUndo) return undefined;
    const node = document.getElementById('task-undo');
    if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest' });
    const timer = window.setTimeout(() => {
      setPushUndo(null);
      setPushUndoTab(null);
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [pushUndo]);
  useEffect(() => {
    if (!pushTaskId) return undefined;
    const node = document.getElementById(`already-done-${pushTaskId}`);
    if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest' });
    return undefined;
  }, [pushTaskId]);
  const [savingPlan, setSavingPlan] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tourChoiceStored, setTourChoiceStored] = useState(() => (
    homeownerTourChoiceStored(client, readStoredFlag(
      typeof localStorage !== 'undefined' ? localStorage : null,
      homeownerTourStorageKey(client && client.id)
    ))
  ));
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date());
  const [expandedProjects, setExpandedProjects] = useState(() => (
    preview && previewProjects[0] ? { [previewProjects[0].id]: true } : {}
  ));
  const [expandedProjectTasks, setExpandedProjectTasks] = useState({}); // Track which project tasks have vendors expanded
  const [projectFilter, setProjectFilter] = useState('active'); // 'active', 'completed', 'all'

  // Tasks view state
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [showFullYear, setShowFullYear] = useState(false);
  const [taskViewMode, setTaskViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [checkinVisible, setCheckinVisible] = useState(null);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTaskData, setNewTaskData] = useState({
    title: '',
    due_date: ''
  });

  // New features state
  const [documents, setDocuments] = useState([]);
  const [documentFolders, setDocumentFolders] = useState([]);
  const [openDocumentFolderId, setOpenDocumentFolderId] = useState(null);
  const [propertyContacts, setPropertyContacts] = useState([]);
  const [vendorFocusId, setVendorFocusId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadingPropertyPhoto, setUploadingPropertyPhoto] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [assistantError, setAssistantError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const assistantInputRef = useRef(null);
  const assistantImageInputRef = useRef(null);
  const imagePreviewGeneration = useRef(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadLoadingOlder, setThreadLoadingOlder] = useState(false);
  const [threadHasOlder, setThreadHasOlder] = useState(false);
  const [threadCursor, setThreadCursor] = useState(null);
  const threadRequested = useRef(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentCategory, setDocumentCategory] = useState('');
  const [previewDocument, setPreviewDocument] = useState(null);

  const [propertyData, setPropertyData] = useState({
    address: client.address || '',
    city: client.city || '',
    state: client.state || 'FL',
    zip_code: client.zip_code || '',
    property_county_fips: client.property_county_fips || '',
    property_county_source: client.property_county_source || '',
    property_type: canonicalPropertyType(client.property_type) || 'single_family',
    features: client.features || []
  });

  const [editingSystems, setEditingSystems] = useState(false);
  const [clientSystems, setClientSystems] = useState([]); // Systems from onboarding
  const [inventoryEditor, setInventoryEditor] = useState(null);
  const [savingInventory, setSavingInventory] = useState(false);
  const [systemData, setSystemData] = useState({
    hvac_count: client.hvac_count || 1,
    hvac_install_years: client.hvac_install_years || [null],
    hvac_types: client.hvac_types || ['Central AC'],
    roof_type: client.roof_type || '',
    roof_install_year: client.roof_install_year || null,
    water_heater_type: client.water_heater_type || '',
    water_heater_install_year: client.water_heater_install_year || null,
    water_source: client.water_source || [],
    irrigation_source: client.irrigation_source || [],
    well_pump_install_year: client.well_pump_install_year || null,
    sewer_type: client.sewer_type || [],
    septic_install_year: client.septic_install_year || null,
    pool_pump_install_year: client.pool_pump_install_year || null,
    pool_heater_install_year: client.pool_heater_install_year || null,
    year_built: client.year_built || null,
    square_feet: client.square_feet || null,
    filter_type: client.filter_type || '',
    hvac_filter_sizes: client.hvac_filter_sizes || [],
  });

  // Ref to track confetti interval for cleanup
  const confettiIntervalRef = useRef(null);
  const completionInFlight = useRef(new Set());
  const hireGate = useVendorHireGate(
    async () => {
      const data = await homeownerPost('/.netlify/functions/vendor-consent', { action: 'status' });
      return data.accepted === true;
    },
    async (pending) => {
      await homeownerPost('/.netlify/functions/vendor-consent', {
        action: 'accept',
        consent_type: 'before_you_hire',
        vendor_id: pending && pending.vendorId ? pending.vendorId : null,
      });
    },
  );

  const propertyTypes = propertyTypeChoices();
  const [homeProfile, setHomeProfile] = useState(() => readHomeProfile(client));
  const [savingHomeProfile, setSavingHomeProfile] = useState(false);
  const [rebuildingTasks, setRebuildingTasks] = useState(false);
  const [tasksReadyHidden, setTasksReadyHidden] = useState(() => (
    tasksReadyDismissed(typeof localStorage === 'undefined' ? null : localStorage, client && client.id)
  ));
  useEffect(() => {
    setTasksReadyHidden(tasksReadyDismissed(
      typeof localStorage === 'undefined' ? null : localStorage,
      client && client.id
    ));
  }, [client && client.id]);

  const showTasksReady = shouldShowTasksReady({
    taskCount: Array.isArray(tasks) ? tasks.length : 0,
    dismissed: tasksReadyHidden,
  });
  const [rebuildNote, setRebuildNote] = useState('');
  const [factsNote, setFactsNote] = useState('');
  const [homePromptOpen, setHomePromptOpen] = useState(false);
  const [homePromptHidden, setHomePromptHidden] = useState(() => (
    homeProfilePromptDismissed(typeof localStorage !== 'undefined' ? localStorage : null, client && client.id)
  ));
  const [homeFactsCardHidden, setHomeFactsCardHidden] = useState(() => (
    homeFactsCardDismissed(typeof localStorage !== 'undefined' ? localStorage : null, client && client.id)
  ));

  const availableFeatures = VISIBLE_FEATURES;

  const documentCategories = [
    'Receipts', 'Invoices', 'Warranties', 'Manuals', 'Permits',
    'Home Reports', 'Wind mitigation', '4-point inspection', 'Roof inspection',
    'Service Records', 'Other'
  ];

  const clearAssistantAttachment = () => {
    imagePreviewGeneration.current += 1;
    setSelectedImage(null);
    setImagePreviewUrl(null);
    const input = assistantImageInputRef.current;
    if (input) input.value = '';
  };

  const restoreAssistantAttachment = (file, url) => {
    if (!file) return;
    const generation = imagePreviewGeneration.current + 1;
    imagePreviewGeneration.current = generation;
    setSelectedImage(file);
    if (url) {
      setImagePreviewUrl(url);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      if (imagePreviewGeneration.current !== generation) return;
      setImagePreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle image selection - Sharp backend handles all format conversions (including HEIC)
  const handleImageSelect = (file) => {
    if (!file) {
      clearAssistantAttachment();
      return;
    }

    const generation = imagePreviewGeneration.current + 1;
    imagePreviewGeneration.current = generation;
    // Set the selected image (Sharp will handle HEIC conversion on backend)
    setSelectedImage(file);

    // Create preview URL using FileReader (more reliable on iOS than createObjectURL)
    const reader = new FileReader();
    reader.onloadend = () => {
      if (imagePreviewGeneration.current !== generation) return;
      setImagePreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Same merge the agent detail panel uses. A system row with data wins.
  const getMergedSystems = () => mergeClientSystems(client, clientSystems);

  const openRegionalTask = (target) => {
    const due = target && String(target.dueDate || '').slice(0, 10);
    const found = (tasks || []).find((task) => {
      if (!task) return false;
      if (target.taskId && task.id === target.taskId) return true;
      const taskDue = String(task.due_date || '').slice(0, 10);
      if (target.seriesId && task.series_id === target.seriesId && taskDue === due) return true;
      const name = task.task_name || (task.task && task.task.title) || '';
      return Boolean(target.title) && name === target.title && taskDue === due;
    });
    setTaskViewMode('list');
    if (target && target.showAll) setShowFullYear(true);
    if (found) {
      setExpandedTaskId(found.id);
      const parsed = due ? due.split('-').map(Number) : [];
      if (parsed.length === 3) setCurrentCalendarMonth(new Date(parsed[0], parsed[1] - 1, 1));
    }
    goToTab('tasks');
    if (found && typeof window !== 'undefined') {
      window.setTimeout(() => {
        const node = document.getElementById(`task-${found.id}`);
        if (node && node.scrollIntoView) node.scrollIntoView({ block: 'center' });
      }, 80);
    }
  };

  const openFindAPro = (trade) => {
    setVendorTradeFilter(trade);
    setSelectedVendorCategory('All');
    setVendorSearchTerm('');
    goToTab('vendors');
  };

  const goToTab = (id) => {
    if (id === 'settings') {
      setMorePane(null);
      setShowMove(false);
    }
    setActiveTab(id);
    if (typeof window === 'undefined' || !window.history) return;
    const path = pathForHomeownerTab(id);
    const onSplitPage = Boolean(homeownerPageFromPath(window.location.pathname));
    if (!path && !onSplitPage) return;
    const next = path || '/';
    if (window.location.pathname !== next) {
      window.history.pushState({ tab: id }, '', next);
    }
  };

  const openInventoryEditor = (system) => {
    if (readOnlyHome) return;
    setInventoryEditor(system
      ? { systemType: system.id, data: system.data || {} }
      : { systemType: '', data: null });
  };

  const openListedHome = async (home) => {
    if (!home || !home.id || switchingHome) return;
    setSwitchingHome(true);
    try {
      await switchHomeownerHome(home.id);
      const loaded = await loadHomeownerClient();
      if (onClientReplaced) onClientReplaced(loaded.client || loaded);
    } catch (error) {
      showToast('Could not switch homes.', 'error');
    } finally {
      setSwitchingHome(false);
    }
  };

  const uploadInventoryPhoto = async (file, systemType) => {
    const label = (INVENTORY_TYPES.find((type) => type.id === systemType) || {}).label || 'Item';
    const upload = await homeownerRecords({
      resource: 'client_documents',
      action: 'upload_url',
      fileName: file.name || `${systemType}.jpg`,
    });
    const contentType = contentTypeForFile(file);
    const uploadResponse = await fetch(upload.signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });
    if (!uploadResponse.ok) throw new Error(await storageErrorMessage(uploadResponse));
    const inserted = await homeownerRecords({
      resource: 'client_documents',
      action: 'insert',
      record: {
        title: `${label} photo`,
        category: 'Other',
        file_name: file.name,
        file_path: upload.path,
        file_type: contentType,
        mime_type: contentType,
        file_size: file.size,
        document_type: 'Other',
      },
    });
    return {
      path: upload.path,
      name: file.name,
      documentId: inserted && inserted.data && inserted.data.id,
    };
  };

  const handleSaveInventory = async (form) => {
    if (!form.systemType) return;
    setSavingInventory(true);
    try {
      let photo = null;
      if (form.photoFile) {
        try {
          photo = await uploadInventoryPhoto(form.photoFile, form.systemType);
        } catch (error) {
          console.error('Error uploading item photo:', error);
          showToast('The item will save. The photo did not upload.', 'warning');
        }
      }
      const existingRow = clientSystems.find((row) => row.system_type === form.systemType);
      const merged = getMergedSystems().find((system) => system.id === form.systemType);
      const existingData = existingRow && existingRow.system_data && Object.keys(existingRow.system_data).length
        ? existingRow.system_data
        : ((merged && merged.data) || {});
      const systemDataPayload = buildSystemData(existingData, { ...form, photo }, new Date().getFullYear());
      await homeownerRecords({
        resource: 'client_systems',
        action: 'upsert',
        record: {
          client_id: client.id,
          agent_id: client.agent_id,
          system_type: form.systemType,
          system_data: systemDataPayload,
        },
      });
      const nextFeatures = featuresAfterInventorySave({
        features: propertyData.features,
        systems: systemsMapFromRows(clientSystems, form.systemType, systemDataPayload),
      });
      const previousFeatures = featuresAfterInventorySave({
        features: propertyData.features,
        systems: systemsMapFromRows(clientSystems),
      });
      await updateHomeownerClient({ features: featuresFromFacts(nextFeatures, client) });
      setPropertyData((prev) => ({ ...prev, features: featuresFromFacts(nextFeatures, client) }));
      client.features = featuresFromFacts(nextFeatures, client);
      await reassignTasks(client.id, client.agent_id, propertyData.property_type, client.features);
      await loadTasks();
      await loadClientSystems();
      setInventoryEditor(null);
      showToast('Item saved.', 'success');
    } catch (error) {
      console.error('Error saving inventory item:', error);
      showToast(planLimitedMessage(error, 'Could not save that item. Please try again.'), error && error.status === 402 ? 'info' : 'error');
    } finally {
      setSavingInventory(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (preview) return;
    loadData();
  }, [client]);

  useEffect(() => {
    if (activeTab !== 'property') return undefined;
    loadClientSystems();
    return undefined;
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const listedHomeId = client && client.id;
  useEffect(() => {
    if (activeTab !== 'settings') return undefined;
    let cancelled = false;
    listHomeownerHomes()
      .then((listed) => {
        if (cancelled) return;
        const homes = (listed && listed.homes) || [];
        setAccountHomes(Array.isArray(homes) ? homes : []);
      })
      .catch(() => {
        if (!cancelled) setAccountHomes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, listedHomeId]);

  useEffect(() => {
    let cancelled = false;
    if (preview) return undefined;
    if (typeof loadHomeownerBilling !== 'function') return undefined;
    Promise.resolve()
      .then(() => loadHomeownerBilling())
      .then((data) => {
        if (cancelled) return;
        const plan = data && data.plan
          ? Object.assign({}, data.plan, { annualEnabled: data.annualEnabled === true })
          : null;
        setHomePlan(plan);
        setCanManagePlan(Boolean(plan && plan.portal));
      })
      .catch(() => {
        if (cancelled) return;
        setCanManagePlan(false);
        setHomePlan(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onPush = (event) => {
      const detail = (event && event.detail) || {};
      if (detail.screen === 'projects') goToTab('projects');
      else if (detail.screen === 'tasks') goToTab('tasks');
      else if (detail.screen === 'home-history') goToTab('home-history');
      loadTasks();
      loadProjects();
    };
    window.addEventListener(PUSH_OPEN_EVENT, onPush);
    return () => window.removeEventListener(PUSH_OPEN_EVENT, onPush);
  }, []);

  useEffect(() => {
    const syncPage = () => {
      const page = homeownerPageFromPath(window.location.pathname);
      if (page) {
        setActiveTab(page);
        return;
      }
      const section = new URLSearchParams(window.location.search).get('section');
      if (section === 'projects' || section === 'tasks' || section === 'overview' || section === 'assistant' || section === 'settings') {
        setActiveTab(section);
        return;
      }
      const stored = window.history.state && window.history.state.tab;
      if (stored) setActiveTab(stored);
    };
    window.addEventListener('popstate', syncPage);
    return () => window.removeEventListener('popstate', syncPage);
  }, []);

  // Update password setup modal visibility when client.password_hash changes
  useEffect(() => {
    setShowPasswordSetup(
      client.password_reset_required || (!client.has_password && !client.password_hash)
    );
  }, [client.has_password, client.password_hash, client.password_reset_required]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // A saved skip or finish stays saved. Otherwise the tour starts before the questionnaire.
  useEffect(() => {
    const stored = homeownerTourChoiceStored(client, readStoredFlag(localStorage, homeownerTourStorageKey(client.id)));
    if (stored) {
      setTourChoiceStored(true);
      return;
    }
    if (shouldAutoStartHomeownerTour({
      hasPassword: !!(client.has_password || client.password_hash),
      passwordDialogOpen: showPasswordSetup,
      choiceStored: false,
    })) {
      setShowTutorial(true);
    }
  }, [client, showPasswordSetup]);

  // Incomplete checklist tasks. Opening Projects clears the badge until the count grows.
  useEffect(() => {
    setIncompleteTaskCount(incompleteProjectTaskCount(projects));
  }, [projects]);

  useEffect(() => {
    // Wait for the first load. The count starts at 0, and shrinking the
    // stored mark against that would bring a cleared badge back.
    if (loading) return undefined;
    const count = incompleteProjectTaskCount(projects);
    const next = nextSeenProjectCount(seenProjectCount, count, activeTab === 'projects');
    if (next !== seenProjectCount) setSeenProjectCount(next);
    if (!projectsSeenKey) return undefined;
    try {
      sessionStorage.setItem(projectsSeenKey, String(next));
    } catch (error) {
      // Private mode can reject storage. The in-memory count still clears the badge.
    }
    return undefined;
  }, [loading, projects, activeTab, seenProjectCount, projectsSeenKey]);

  useEffect(() => {
    const visible = projectTabBadgeCount(incompleteTaskCount, seenProjectCount, activeTab === 'projects');
    setAppBadge(visible);
  }, [incompleteTaskCount, seenProjectCount, activeTab]);

  // Cleanup confetti interval on unmount
  useEffect(() => {
    return () => {
      if (confettiIntervalRef.current) {
        clearInterval(confettiIntervalRef.current);
      }
    };
  }, []);

  const loadData = async () => {
    await Promise.all([
      loadTasks(),
      loadVendors(),
      loadHomeownerPros(),
      loadAgent(),
      loadNotificationPrefs(),
      loadDocuments(),
      loadConversations(),
      loadProjects(),
      loadClientSystems()
    ]);
    setLoading(false);
    loadPropertyContacts();
  };

  const loadClientSystems = async () => {
    try {
      const { data } = await homeownerRecords({ resource: 'client_systems', action: 'list' });
      setClientSystems(data || []);
    } catch (error) {
      console.error('Error loading client systems:', error);
    }
  };

  const loadTasks = async (change) => {
    try {
      const result = await homeownerTasks();
      const data = result.data || [];
      setTasks((current) => mergeLoadedTasks(current, data, change));
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };


  const loadVendors = async () => {
    try {
      const { data } = await homeownerRecords({ resource: 'vendors', action: 'list' });
      setVendors(data || []);
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadHomeownerPros = async () => {
    try {
      const { data } = await homeownerRecords({ resource: 'homeowner_pros', action: 'list' });
      setHomeownerPros(data || []);
    } catch (error) {
      console.error('Error loading saved providers:', error);
    }
  };

  const saveHomeownerPro = async (record) => {
    const editing = proSheet && proSheet.pro && proSheet.pro.id;
    try {
      await homeownerRecords({
        resource: 'homeowner_pros',
        action: editing ? 'update' : 'insert',
        id: editing || undefined,
        record,
      });
      setProSheet(null);
      await loadHomeownerPros();
      showToast(editing ? 'Provider updated.' : 'Provider saved.', 'success');
    } catch (error) {
      const detail = error && error.message && error.message !== 'Request failed' ? error.message : '';
      showToast(detail || 'Could not save that provider.', 'error');
    }
  };

  const deleteHomeownerPro = async (pro) => {
    if (!pro || !window.confirm('Delete this saved provider?')) return;
    try {
      await homeownerRecords({ resource: 'homeowner_pros', action: 'delete', id: pro.id });
      await loadHomeownerPros();
    } catch (error) {
      showToast('Could not delete that provider.', 'error');
    }
  };

  const trackVendorClick = async (vendorId, clickType) => {
    try {
      await homeownerRecords({
        resource: 'vendor_clicks',
        action: 'insert',
        vendor_id: vendorId,
        click_type: clickType,
      });
    } catch (error) {
      console.error('Error tracking vendor click:', error);
    }
  };

  const gateVendorContact = (event, vendor, kind, newTab) => {
    hireGate.requestContact(event, {
      href: event.currentTarget.href,
      newTab,
      vendorId: vendor.id,
      kind,
      onAllowed: () => trackVendorClick(vendor.id, kind),
    });
  };

  const loadProjects = async () => {
    try {
      const { data } = await homeownerRecords({ resource: 'projects', action: 'list' });
      setProjects(data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const projectVendorCatalog = () => (shownAgentVendors || []).concat(homeownerPros || []);

  const projectVendorOptions = () => {
    const pros = (homeownerPros || []).map((pro) => ({
      value: `homeowner:${pro.id}`,
      label: pro.name,
      vendor: { id: pro.id, source: 'homeowner', name: pro.name, phone: pro.phone || '' },
    }));
    const agentVendors = (shownAgentVendors || []).map((vendor) => ({
      value: `agent:${vendor.id}`,
      label: vendor.name,
      vendor: { id: vendor.id, source: 'agent', name: vendor.name, phone: vendor.phone || '' },
    }));
    return pros.concat(agentVendors);
  };

  const saveProjectTasks = async (projectId, tasks) => {
    const stored = tasks.map((task) => {
      const next = Object.assign({}, task);
      delete next.photos;
      return next;
    });
    await homeownerRecords({
      resource: 'projects',
      action: 'update_tasks',
      id: projectId,
      tasks: stored,
    });
  };

  const createHomeownerProject = async (draft) => {
    setSavingProject(true);
    try {
      const drafted = draft.tasks || [];
      const groups = drafted.map((task) => ({
        id: task.id,
        files: (Array.isArray(task.photos) ? task.photos : []).filter(Boolean),
      }));
      const warm = groups.map((group) => warmTaskPhoto(group.files[0]));
      const created = await homeownerRecords({
        resource: 'projects',
        action: 'create',
        title: draft.title,
        description: draft.description,
        due_date: draft.due_date,
        tasks: draft.tasks.map((task) => ({
          id: task.id,
          title: task.title,
          vendors: task.vendors,
        })),
      });
      const project = created && created.data;
      if (!project || !project.id) throw new Error('Could not create that project.');
      await Promise.all(groups.map((group, index) => (
        group.files.length
          ? uploadHomeownerTaskPhotos(project.id, group.id, group.files, null, warm[index])
          : null
      )));
      setShowProjectForm(false);
      await loadProjects();
      showToast('Project saved.', 'success');
    } catch (error) {
      console.error('Error creating project:', error);
      showToast(error.message || 'Could not create that project.', error && error.status === 402 ? 'info' : 'error');
    } finally {
      setSavingProject(false);
    }
  };

  const changeProjectVendors = async (project, task, nextTask, errorMessage) => {
    if (errorMessage) {
      showToast(errorMessage, 'info');
      return;
    }
    const tasks = project.tasks.map((item) => (item.id === task.id ? nextTask : item));
    setProjects((current) => current.map((item) => (item.id === project.id ? Object.assign({}, item, { tasks }) : item)));
    try {
      await saveProjectTasks(project.id, tasks);
    } catch (error) {
      await loadProjects();
      showToast(error.message || VENDOR_LIMIT_MESSAGE, 'error');
    }
  };

  const addHomeownerTaskPhoto = async (project, task, fileOrFiles, error) => {
    if (error) {
      showToast(error.message || 'Could not use that photo.', 'error');
      return;
    }
    const files = (Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]).filter(Boolean);
    if (!files.length) return;
    const key = `${project.id}:${task.id}`;
    setPhotoBusyKey(key);
    try {
      await uploadHomeownerTaskPhotos(project.id, task.id, files, (photo) => {
        setProjects((current) => current.map((item) => {
          if (item.id !== project.id) return item;
          const tasks = item.tasks.map((row) => {
            if (row.id !== task.id) return row;
            const photos = (row.photos || []).concat([Object.assign({ removable: true }, photo)]);
            return Object.assign({}, row, { photos });
          });
          return Object.assign({}, item, { tasks });
        }));
      });
    } catch (uploadError) {
      console.error('Error uploading task photo:', uploadError);
      showToast(uploadError.message || 'Could not upload that photo.', uploadError.status === 402 ? 'info' : 'error');
    } finally {
      setPhotoBusyKey('');
    }
  };

  const removeHomeownerTaskPhoto = async (project, task, photo) => {
    try {
      await deleteHomeownerTaskPhoto(project.id, task.id, photo.id);
      setProjects((current) => current.map((item) => {
        if (item.id !== project.id) return item;
        const tasks = item.tasks.map((row) => {
          if (row.id !== task.id) return row;
          return Object.assign({}, row, { photos: (row.photos || []).filter((shot) => shot.id !== photo.id) });
        });
        return Object.assign({}, item, { tasks });
      }));
    } catch (error) {
      showToast(error.message || 'Could not remove that photo.', 'error');
    }
  };

  refreshHomeRef.current = () => Promise.all([loadTasks(), loadProjects()]);

  useEffect(() => {
    const onRefresh = () => { refreshHomeRef.current(); };
    window.addEventListener(APP_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(APP_REFRESH_EVENT, onRefresh);
  }, []);

  useEffect(() => {
    if (activeTab !== 'tasks' && activeTab !== 'projects') return undefined;
    const scroller = document.querySelector('.app-scroll');
    return installPullRefresh(scroller, () => {
      setListRefreshing(true);
      Promise.resolve(refreshHomeRef.current()).finally(() => setListRefreshing(false));
    });
  }, [activeTab]);

  const loadAgent = async () => {
    try {
      const data = await loadHomeownerClient();
      setAgent(data.agent || null);
      setJoinRequest((data && data.joinRequest) || null);
    } catch (error) {
      console.error('Error loading agent:', error);
    }
  };

  const loadNotificationPrefs = async () => {
    try {
      const result = await homeownerRecords({ resource: 'notification_preferences', action: 'get' });
      setNotifAudience((result && result.audience) || 'sponsorless');
      setNotifPrefs((result && result.data) || { ...HOMEOWNER_PREF_DEFAULTS });
    } catch (error) {
      console.error('Error loading notification preferences:', error);
      setNotifPrefs((current) => current || { ...HOMEOWNER_PREF_DEFAULTS });
    }
  };

  const loadDocuments = async () => {
    try {
      const result = await homeownerRecords({ resource: 'client_documents', action: 'list' });
      setDocuments((result && result.data) || []);
      setDocumentFolders((result && result.folders) || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const runDocumentChange = async (body, failure) => {
    if (readOnlyHome) return;
    try {
      await homeownerRecords(body);
      await loadDocuments();
    } catch (error) {
      console.error(failure, error);
      showToast((error && error.message) || failure, 'error');
    }
  };

  const loadPropertyContacts = async () => {
    try {
      if (typeof fetch !== 'function') return;
      const token = await getHomeownerToken();
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(getApiUrl('/.netlify/functions/client-property-contacts'), { headers });
      if (!res || !res.ok || typeof res.json !== 'function') return;
      const json = await res.json();
      setPropertyContacts((json && json.data) || []);
    } catch (error) {
      console.error('Error loading property contacts:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const { data } = await homeownerRecords({ resource: 'ai_conversations', action: 'list' });
      setConversations(data || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const applyThreadResult = (result, mode) => {
    const next = rowsToMessages((result && result.data) || []);
    setThreadHasOlder(Boolean(result && result.hasOlder));
    setThreadCursor((result && result.nextBefore) || null);
    setChatMessages((current) => {
      if (mode === 'older') return mergeOlder(current, next);
      const local = current.filter((msg) => !msg.rowId);
      return next.concat(local);
    });
    return next;
  };

  useEffect(() => {
    if (activeTab !== 'assistant' || threadRequested.current) return undefined;
    let cancelled = false;
    threadRequested.current = true;
    setThreadLoading(true);
    homeownerRecords({ resource: 'ai_conversations', action: 'page' })
      .then((result) => {
        if (!cancelled) applyThreadResult(result, 'replace');
      })
      .catch((error) => {
        threadRequested.current = false;
        console.error('Error loading chat:', error);
      })
      .finally(() => {
        if (!cancelled) setThreadLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab]);

  const loadOlderChat = async () => {
    if (!threadHasOlder || threadLoadingOlder || !threadCursor) return;
    setThreadLoadingOlder(true);
    try {
      const result = await homeownerRecords({
        resource: 'ai_conversations',
        action: 'page',
        before: threadCursor.createdAt,
        beforeId: threadCursor.id,
      });
      applyThreadResult(result, 'older');
    } catch (error) {
      console.error('Error loading older chat:', error);
    } finally {
      setThreadLoadingOlder(false);
    }
  };

  const searchChat = async (query) => {
    const result = await homeownerRecords({ resource: 'ai_conversations', action: 'search', query });
    return (result && result.matches) || [];
  };

  const revealChat = async (hit) => {
    const messageId = hit && (hit.messageId || `${hit.id}:assistant`);
    if (!messageId || !threadHasOlder || !threadCursor) return;
    let cursor = threadCursor;
    let more = threadHasOlder;
    let guard = 0;
    while (more && cursor && guard < 20) {
      guard += 1;
      const result = await homeownerRecords({
        resource: 'ai_conversations',
        action: 'page',
        before: cursor.createdAt,
        beforeId: cursor.id,
      });
      const next = applyThreadResult(result, 'older');
      if (next.some((msg) => msg.id === messageId)) return;
      more = Boolean(result && result.hasOlder);
      cursor = (result && result.nextBefore) || null;
      if (!next.length) return;
    }
  };

  const handleStartFresh = async () => {
    const marker = {
      id: `local-fresh-${Date.now()}`,
      fresh: true,
      role: 'fresh',
      timestamp: new Date(),
    };
    setChatMessages((prev) => prev.concat(marker));
    try {
      await homeownerRecords({ resource: 'ai_conversations', action: 'fresh' });
    } catch (error) {
      setChatMessages((prev) => prev.filter((msg) => msg.id !== marker.id));
      showToast('Could not start fresh. Try again.', 'error');
    }
  };


  const handlePasswordSetup = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      showToast('Password must be at least 8 characters long.', 'warning');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match.', 'warning');
      return;
    }
    try {
      const result = await setHomeownerPassword(newPassword);
      showToast('Password set successfully!', 'success');
      setShowPasswordSetup(false);
      Object.assign(client, result.client || {});
      client.has_password = true;
      client.password_hash = undefined;
      client.account_created = true;
    } catch (error) {
      console.error('Error setting password:', error);
      showToast('Error setting password. Please try again.', 'error');
    }
  };

  const handlePushOut = async (taskId) => {
    if (!taskId || pushing) return;
    setPushing(true);
    try {
      const result = await homeownerPost('/.netlify/functions/homeowner-plan', {
        action: 'push-out',
        taskId,
        doneDate: pushDate,
      });
      setPushUndo(result && result.undo ? result.undo : null);
      setPushUndoTab(activeTab);
      setPushTaskId(null);
      showToast('Marked as already done. Use Undo if that date was wrong.', 'success', 6000);
      await loadTasks();
    } catch (error) {
      console.error('Error pushing task out:', error);
      showToast('Could not update that task.', 'error');
    } finally {
      setPushing(false);
    }
  };

  const handleSkipYear = async (taskId) => {
    if (!taskId || pushing) return;
    setPushing(true);
    try {
      await homeownerPost('/.netlify/functions/homeowner-plan', {
        action: 'skip-year',
        taskId,
        skipScope: 'year',
      });
      showToast('Skipped for this year.', 'success');
      await loadTasks();
    } catch (error) {
      console.error('Error skipping task year:', error);
      showToast('Could not skip that task.', 'error');
    } finally {
      setPushing(false);
    }
  };

  const handleUndoPush = async () => {
    if (!pushUndo) return;
    const undo = pushUndo;
    setPushUndo(null);
    try {
      await homeownerPost('/.netlify/functions/homeowner-plan', { action: 'undo-push', undo });
      showToast('Restored that task.', 'success');
      await loadTasks();
    } catch (error) {
      setPushUndo(undo);
      console.error('Error undoing push-out:', error);
      showToast('Could not undo that change.', 'error');
    }
  };

  const saveMaintenancePlan = async (fields) => {
    if (readOnlyHome) return false;
    setSavingPlan(true);
    try {
      const next = Object.assign({}, fields);
      if (client.onboarding_completed_at) delete next.onboarding_completed_at;
      if (Object.keys(next).length === 0) {
        showToast('Add an answer to save. Leaving this blank keeps the current plan.', 'info');
        return false;
      }
      const updated = await updateHomeownerClient(next);
      if (updated && updated.client) Object.assign(client, updated.client);
      await homeownerPost('/.netlify/functions/homeowner-plan', { action: 'apply-answers' });
      await reassignTasks(client.id, client.agent_id, propertyData.property_type, featuresFromFacts(propertyData.features, client));
      await loadTasks();
      showToast('Maintenance plan saved.', 'success');
      return true;
    } catch (error) {
      console.error('Error saving maintenance plan:', error);
      showToast('Could not save the maintenance plan.', 'error');
      return false;
    } finally {
      setSavingPlan(false);
    }
  };

  const handleCompleteTask = async (taskId, currentStatus) => {
    if (readOnlyHome) {
      showToast('This home is archived and can only be viewed.', 'info');
      return;
    }
    if (completionInFlight.current.has(taskId)) return;
    completionInFlight.current.add(taskId);
    try {
      const newStatus = !currentStatus;
      const completedAt = newStatus ? new Date().toISOString() : null;

      Haptics.impact({ style: newStatus ? ImpactStyle.Medium : ImpactStyle.Light }).catch(() => {
        devLog('Haptics not supported on this platform');
      });

      const currentTask = tasks.find((task) => task && task.id === taskId);
      const currentTitle = currentTask && String(currentTask.task_name || '').trim();
      const completionBody = {
        completed: newStatus,
        completed_at: completedAt,
      };
      if (currentTitle) completionBody.task_name = currentTitle;
      await homeownerTasks(`/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify(completionBody),
      });

      const change = { taskId, completed: newStatus, completedAt, removeIds: [], addTasks: [] };
      setTasks((current) => applyTaskCompletion(current, change));
      if (newStatus) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#59c4c4', '#4ab3b3', '#10b981', '#3b82f6']
        });
      }
      await loadTasks(change);
    } catch (error) {
      console.error('Error updating task:', error);
      showToast('Error updating task. Please try again.', 'error');
    } finally {
      completionInFlight.current.delete(taskId);
    }
  };

  const handleToggleProjectTask = async (projectId, taskId) => {
    if (readOnlyHome) {
      showToast('This home is archived and can only be viewed.', 'info');
      return;
    }
    try {
      const project = projects.find(p => p.id === projectId);
      if (!project) return;

      const wasComplete = project.tasks.every(t => t.completed);
      const task = project.tasks.find(t => t.id === taskId);
      const taskBeingCompleted = task && !task.completed;

      const updatedTasks = project.tasks.map(task =>
        task.id === taskId ? { ...task, completed: !task.completed } : task
      );

      const isNowComplete = updatedTasks.every(t => t.completed);

      devLog('Project Task Toggle:', {
        projectId,
        taskId,
        wasComplete,
        isNowComplete,
        taskBeingCompleted,
        willTriggerGrandConfetti: !wasComplete && isNowComplete
      });

      // Optimistically update UI first
      setProjects(prevProjects =>
        prevProjects.map(p =>
          p.id === projectId ? { ...p, tasks: updatedTasks } : p
        )
      );

      try {
        await homeownerRecords({
          resource: 'projects',
          action: 'update_tasks',
          id: projectId,
          tasks: updatedTasks,
        });
      } catch (error) {
        await loadProjects();
        throw error;
      }

      if (taskBeingCompleted && !isNowComplete) {
        confetti({
          particleCount: 50,
          spread: 50,
          origin: { y: 0.6 },
          colors: ['#59c4c4', '#4ab3b3', '#10b981']
        });
      }

      if (!wasComplete && isNowComplete) {
        devLog('PROJECT COMPLETE! Triggering grand confetti celebration!');

        // Grand confetti celebration for project completion
        const duration = 3 * 1000;
        const animationEnd = Date.now() + duration;
        const defaults = {
          startVelocity: 30,
          spread: 360,
          ticks: 60,
          zIndex: 999999,
          colors: ['#59c4c4', '#4ab3b3', '#10b981', '#3b82f6', '#a855f7', '#f59e0b', '#ef4444']
        };

        function randomInRange(min, max) {
          return Math.random() * (max - min) + min;
        }

        // Clear any existing confetti interval before starting new one
        if (confettiIntervalRef.current) {
          clearInterval(confettiIntervalRef.current);
        }

        confettiIntervalRef.current = setInterval(function() {
          const timeLeft = animationEnd - Date.now();

          if (timeLeft <= 0) {
            clearInterval(confettiIntervalRef.current);
            confettiIntervalRef.current = null;
            return;
          }

          const particleCount = 50 * (timeLeft / duration);
          confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }));
          confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }));
        }, 250);

        if (agent) {
          homeownerPost('/.netlify/functions/send-project-completion', { projectId }).catch((emailError) => {
            console.error('Error sending completion email:', emailError);
          });
        }
      }
    } catch (error) {
      console.error('Error updating project task:', error);
      showToast('Error updating task. Please try again.', 'error');
    }
  };

  const handleSaveTaskNote = async (taskId) => {
    try {
      await homeownerTasks(`/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify({ client_notes: taskNoteText })
      });
      setTasks((current) => current.map((task) => (
        task.id === taskId ? Object.assign({}, task, { client_notes: taskNoteText }) : task
      )));
      setEditingTaskNote(null);
      setTaskNoteText('');
      showToast('Note saved!', 'success');
      loadTasks();
    } catch (error) {
      console.error('Error saving note:', error);
      showToast('Error saving note. Please try again.', 'error');
    }
  };

  const handleSnoozeDays = async (days) => {
    if (readOnlyHome) return;
    const taskId = snoozeModal.taskId;
    if (!taskId) return;
    const snoozedUntil = snoozeUntil(days);
    try {
      await homeownerTasks(`/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify({ snoozed_until: snoozedUntil })
      });
      setTasks((prev) => prev.map((task) => (
        task.id === taskId ? Object.assign({}, task, { snoozed_until: snoozedUntil }) : task
      )));
      setSnoozeModal({ show: false, taskId: null, date: '' });
      showToast('Task snoozed!', 'success');
    } catch (error) {
      console.error('Error snoozing task:', error);
      showToast('Could not snooze that chore.', 'error');
    }
  };

  const handleNotForHome = async () => {
    if (readOnlyHome) return;
    const taskId = dismissModal.taskId;
    const reason = String(dismissModal.reason || '').trim();
    if (!taskId || !reason) {
      showToast('Add a short reason.', 'info');
      return;
    }
    try {
      await homeownerTasks(`/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          dismissed_at: new Date().toISOString(),
          dismiss_reason: reason,
        }),
      });
      setTasks((prev) => prev.filter((task) => task.id !== taskId));
      setDismissModal({ show: false, taskId: null, reason: '' });
      showToast('Hidden. We saved the reason.', 'success');
    } catch (error) {
      console.error('Error hiding task:', error);
      showToast('Could not hide that chore.', 'error');
    }
  };

  const handleCompletionPhoto = async (task, file) => {
    if (readOnlyHome || !file || !task) return;
    try {
      const ticket = await homeownerRecords({
        resource: 'client_documents',
        action: 'upload_url',
        fileName: file.name || 'chore.jpg',
      });
      if (!ticket || !ticket.signedUrl || !ticket.path) throw new Error('Could not start the photo upload.');
      const uploaded = await fetch(ticket.signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'image/jpeg' },
        body: file,
      });
      if (!uploaded.ok) throw new Error('The photo did not upload.');
      await homeownerRecords({
        resource: 'client_documents',
        action: 'insert',
        record: {
          title: task.task_name || (task.task && task.task.title) || 'Chore photo',
          file_name: file.name || 'chore.jpg',
          file_path: ticket.path,
          file_type: 'image',
          mime_type: file.type || 'image/jpeg',
          file_size: file.size,
          document_type: 'Chore photo',
        },
      });
      await homeownerTasks(`/${task.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ completion_photo_path: ticket.path }),
      });
      setTasks((current) => current.map((row) => (
        row.id === task.id ? Object.assign({}, row, { completion_photo_path: ticket.path }) : row
      )));
      showToast('Photo saved with this home.', 'success');
    } catch (error) {
      console.error('Error saving chore photo:', error);
      showToast('The chore is done. The photo did not save.', 'error');
    }
  };

  const handleRescheduleTask = async () => {
    if (!rescheduleModal.date) {
      showToast('Please select a date.', 'warning');
      return;
    }
    try {
      const taskId = rescheduleModal.taskId;
      const dueDate = rescheduleModal.date;
      await homeownerTasks(`/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify({ due_date: dueDate })
      });
      setTasks((current) => current.map((task) => (
        task.id === taskId ? Object.assign({}, task, { due_date: dueDate }) : task
      )));
      setRescheduleModal({ show: false, taskId: null, date: '' });
      showToast('Task rescheduled', 'success');
      loadTasks();
    } catch (error) {
      console.error('Error rescheduling task:', error);
      showToast('Error rescheduling task. Please try again.', 'error');
    }
  };

  const handleSetReminder = async () => {
    if (!reminderModal.date) {
      showToast('Please select a date.', 'warning');
      return;
    }
    try {
      const taskId = reminderModal.taskId;
      const reminderOn = reminderModal.date;
      await homeownerTasks(`/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify({ reminder_on: reminderOn })
      });
      setTasks((current) => current.map((task) => (
        task.id === taskId ? Object.assign({}, task, { reminder_on: reminderOn }) : task
      )));
      setReminderModal({ show: false, taskId: null, date: '' });
      showToast('Reminder set', 'success');
      loadTasks();
    } catch (error) {
      console.error('Error setting reminder:', error);
      showToast('Error setting reminder. Please try again.', 'error');
    }
  };

  const handleAddTask = async () => {
    if (readOnlyHome) return;
    if (!newTaskData.title.trim()) {
      showToast('Please enter a task title', 'warning');
      return;
    }
    if (!newTaskData.due_date) {
      showToast('Please select a due date', 'warning');
      return;
    }

    try {
      const title = newTaskData.title.trim();
      const duplicate = tasks.some((task) => !task.task_id && (task.task_name === title || task.task?.title === title) && task.due_date === newTaskData.due_date);
      if (duplicate) {
        showToast('A task with this name and date already exists', 'warning');
        return;
      }

      await homeownerTasks('', {
        method: 'POST',
        body: JSON.stringify({
          task_name: title,
          due_date: newTaskData.due_date
        })
      });

      setShowAddTaskModal(false);
      setNewTaskData({
        title: '',
        due_date: ''
      });
      showToast('Task added successfully!', 'success');
      loadTasks();
    } catch (error) {
      console.error('Error adding task:', error);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      console.error('Error details:', error.details);
      const unpaid = error && error.status === 402;
      showToast(planLimitedMessage(error, `Error adding task: ${error.message || 'Please try again.'}`), unpaid ? 'info' : 'error');
      if (unpaid) {
        setShowAddTaskModal(false);
        setNewTaskData({ title: '', due_date: '' });
      }
    }
  };

  const handleSaveOwnTask = async () => {
    if (!ownTaskEdit || readOnlyHome) return;
    const title = String(ownTaskEdit.title || '').trim();
    if (!title) {
      showToast('Please enter a task title', 'warning');
      return;
    }
    if (!ownTaskEdit.due_date) {
      showToast('Please select a due date', 'warning');
      return;
    }
    try {
      await homeownerTasks(`/${ownTaskEdit.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ task_name: title, due_date: ownTaskEdit.due_date }),
      });
      setTasks((current) => current.map((task) => (
        task.id === ownTaskEdit.id ? Object.assign({}, task, { task_name: title, due_date: ownTaskEdit.due_date }) : task
      )));
      setOwnTaskEdit(null);
      showToast('Task updated', 'success');
    } catch (error) {
      console.error('Error updating task:', error);
      showToast('Could not update that task.', 'error');
    }
  };

  const handleDeleteOwnTask = async (taskId) => {
    if (!taskId || readOnlyHome) return;
    try {
      await homeownerTasks(`/${taskId}`, { method: 'DELETE' });
      setTasks((current) => current.filter((task) => task.id !== taskId));
      setOwnTaskDeleteId(null);
      setExpandedTaskId(null);
      showToast('Task deleted', 'success');
    } catch (error) {
      console.error('Error deleting task:', error);
      showToast('Could not delete that task.', 'error');
    }
  };

  const handleUpdateNotifPrefs = async (updates) => {
    try {
      // Validate updates before saving
      const validatedUpdates = { ...updates };
      
      // Validate boolean fields
      const booleanFields = HOMEOWNER_BOOL_FIELDS;
      booleanFields.forEach(field => {
        if (field in validatedUpdates && typeof validatedUpdates[field] !== 'boolean') {
          throw new Error(`${field} must be a boolean value`);
        }
      });
      
      // Validate reminder_days_before
      if ('reminder_days_before' in validatedUpdates) {
        const days = validatedUpdates.reminder_days_before;
        if (typeof days !== 'number' || !Number.isInteger(days) || isNaN(days) || days < 1 || days > 14) {
          throw new Error('Reminder days must be a number between 1 and 14');
        }
      }
      
      const result = await homeownerRecords({
        resource: 'notification_preferences',
        action: 'upsert',
        record: validatedUpdates,
      });
      if (result && result.audience) setNotifAudience(result.audience);
      setNotifPrefs((result && result.data) || { ...notifPrefs, ...validatedUpdates });
      showToast('Notification preferences updated!', 'success');
    } catch (error) {
      console.error('Error updating preferences:', error);
      showToast(error.message || 'Error updating preferences. Please try again.', 'error');
    }
  };

  const handleInventoryFeatureToggle = async (featureId) => {
    const previous = propertyData.features;
    const newFeatures = toggleFeature(previous, featureId);
    setPropertyData((prev) => ({ ...prev, features: newFeatures }));
    try {
      await updateHomeownerClient({ features: newFeatures });
      client.features = newFeatures;
      await reassignTasks(client.id, client.agent_id, propertyData.property_type, newFeatures);
      await loadTasks();
    } catch (error) {
      console.error('Error updating features:', error);
      setPropertyData((prev) => ({ ...prev, features: previous }));
      showToast(planLimitedMessage(error, 'Error updating features. Please try again.'), error && error.status === 402 ? 'info' : 'error');
    }
  };

  const handleFeatureToggle = async (featureId) => {
    if (readOnlyHome) return;
    const newFeatures = toggleFeature(propertyData.features, featureId);
    setPropertyData({ ...propertyData, features: newFeatures });
    try {
      await updateHomeownerClient({ features: newFeatures });
      setLoading(true);
      await reassignTasks(client.id, client.agent_id, propertyData.property_type, newFeatures);
      await loadTasks();
      setLoading(false);
      client.features = newFeatures;
      showToast('Home features updated! Tasks have been reassigned.', 'success');
    } catch (error) {
      console.error('Error updating features:', error);
      showToast(planLimitedMessage(error, 'Error updating features. Please try again.'), error && error.status === 402 ? 'info' : 'error');
      setLoading(false);
    }
  };

  const handlePropertyPhotoChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;

    const contentType = contentTypeForFile(file);
    const allowedPhotoTypes = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif']);
    if (!allowedPhotoTypes.has(contentType)) {
      showToast('Use a JPG, PNG, or HEIC photo.', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('Photo must be 10 MB or less.', 'error');
      return;
    }

    setUploadingPropertyPhoto(true);
    try {
      const upload = await homeownerRecords({
        resource: 'property_photo',
        action: 'upload_url',
        fileName: file.name,
      });
      const uploadResponse = await fetch(upload.signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': upload.contentType || contentType },
        body: await file.arrayBuffer(),
      });
      if (!uploadResponse.ok) throw new Error(await storageErrorMessage(uploadResponse));

      await updateHomeownerClient({ property_photo_url: upload.publicUrl });
      client.property_photo_url = upload.publicUrl;
      showToast('Property photo updated.', 'success');
    } catch (error) {
      console.error('Error uploading property photo:', error);
      showToast(error.message || 'Could not upload that photo.', 'error');
    } finally {
      setUploadingPropertyPhoto(false);
    }
  };

  const handleSaveProperty = async () => {
    if (propertyData.county_pending) {
      showToast('Pick the county for this ZIP.', 'error');
      return;
    }
    try {
      await updateHomeownerClient({
        address: propertyData.address,
        city: propertyData.city,
        state: propertyData.state,
        zip_code: propertyData.zip_code,
        property_county_fips: propertyData.property_county_fips || null,
        property_county_source: propertyData.property_county_source || null,
        property_type: propertyData.property_type
      });
      Object.assign(client, propertyData);
      setEditingProperty(false);
      await reassignTasks(client.id, client.agent_id, propertyData.property_type, propertyData.features);
      try {
        await homeownerPost('/.netlify/functions/homeowner-plan', { action: 'apply-answers' });
      } catch (planError) {
        console.error('Plan adjustments were not applied yet', planError);
      }
      await loadTasks();
      showToast('Property information updated!', 'success');
    } catch (error) {
      console.error('Error updating property:', error);
      showToast('Error updating property. Please try again.', 'error');
    }
  };

  // Handle HVAC unit count changes
  const handleHvacCountChange = (count) => {
    const newCount = parseInt(count);
    setSystemData(prev => {
      const newYears = [...prev.hvac_install_years];
      const newTypes = [...prev.hvac_types];

      while (newYears.length < newCount) {
        newYears.push(null);
        newTypes.push('Central AC');
      }
      while (newYears.length > newCount) {
        newYears.pop();
        newTypes.pop();
      }

      return {
        ...prev,
        hvac_count: newCount,
        hvac_install_years: newYears,
        hvac_types: newTypes
      };
    });
  };

  // Update individual HVAC unit data
  const handleHvacUnitChange = (index, field, value) => {
    setSystemData(prev => {
      const newData = field === 'year' ? [...prev.hvac_install_years] : [...prev.hvac_types];
      newData[index] = field === 'year' ? (value ? parseInt(value) : null) : value;

      return {
        ...prev,
        [field === 'year' ? 'hvac_install_years' : 'hvac_types']: newData
      };
    });
  };

  // Handle checkbox arrays (water_source, sewer_type)
  const handleArrayCheckbox = (field, value) => {
    setSystemData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  // Save system information
  const handleSaveSystems = async () => {
    try {
      await updateHomeownerClient({
        hvac_count: systemData.hvac_count,
        hvac_install_years: systemData.hvac_install_years,
        hvac_types: systemData.hvac_types,
        roof_type: systemData.roof_type,
        roof_install_year: systemData.roof_install_year,
        water_heater_type: systemData.water_heater_type,
        water_heater_install_year: systemData.water_heater_install_year,
        water_source: systemData.water_source,
        well_pump_install_year: systemData.well_pump_install_year,
        sewer_type: systemData.sewer_type,
        septic_install_year: systemData.septic_install_year,
        pool_pump_install_year: systemData.pool_pump_install_year,
        pool_heater_install_year: systemData.pool_heater_install_year,
        year_built: systemData.year_built,
        square_feet: systemData.square_feet,
        lot_size: systemData.lot_size
      });

      // Update local client object
      Object.assign(client, systemData);
      setEditingSystems(false);
      await reassignTasks(client.id, client.agent_id, propertyData.property_type, featuresFromFacts(propertyData.features, systemData));
      await loadTasks();
      showToast('System information updated!', 'success');
    } catch (error) {
      console.error('Error updating systems:', error);
      showToast('Error updating system information. Please try again.', 'error');
    }
  };

  const saveHomeFacts = async () => {
    if (readOnlyHome) return;
    setFactsNote('');
    const merged = Object.assign({}, homeProfile, systemData, {
      features: propertyData.features,
    });
    const patch = Object.assign({}, homeProfileSavePatch(merged), homeFactsPatch(merged));
    try {
      await updateHomeownerClient(patch);
      Object.assign(client, patch);
      setPropertyData((prev) => ({ ...prev, features: patch.features }));
      await reassignTasks(client.id, client.agent_id, propertyData.property_type, patch.features);
      try {
        await homeownerPost('/.netlify/functions/homeowner-plan', { action: 'apply-answers' });
      } catch (planError) {
        console.error('Plan adjustments were not applied yet', planError);
      }
      await loadTasks();
      setFactsNote('Saved');
      showToast('Saved', 'success');
    } catch (error) {
      console.error('Error saving home facts:', error);
      setFactsNote('Could not save.');
      showToast('Could not save those home facts.', 'error');
    }
  };

  const rebuildHomeTasks = async () => {
    if (readOnlyHome || rebuildingTasks) return;
    setRebuildingTasks(true);
    setRebuildNote('');
    try {
      await reassignTasks(client.id, client.agent_id, client.property_type, client.features || []);
      try {
        await homeownerPost('/.netlify/functions/homeowner-plan', { action: 'apply-answers' });
      } catch (planError) {
        console.error('Plan adjustments were not applied yet', planError);
      }
      await loadTasks();
      setRebuildNote('Tasks rebuilt');
      showToast('Tasks rebuilt', 'success');
    } catch (error) {
      console.error('Error rebuilding tasks:', error);
      setRebuildNote('Could not rebuild tasks.');
      showToast('Could not rebuild tasks.', 'error');
    } finally {
      setRebuildingTasks(false);
    }
  };

  const saveHomeProfile = async (next) => {
    if (readOnlyHome) return;
    const profile = next || homeProfile;
    setSavingHomeProfile(true);
    try {
      const patch = homeProfileSavePatch(profile);
      await updateHomeownerClient(patch);
      Object.assign(client, patch);
      setHomeProfile(readHomeProfile(client));
      setHomePromptOpen(false);
      await reassignTasks(client.id, client.agent_id, propertyData.property_type, propertyData.features);
      try {
        await homeownerPost('/.netlify/functions/homeowner-plan', { action: 'apply-answers' });
      } catch (planError) {
        console.error('Plan adjustments were not applied yet', planError);
      }
      await loadTasks();
      showToast('Home details saved.', 'success');
    } catch (error) {
      console.error('Error saving home details:', error);
      showToast('Could not save those home details.', 'error');
    } finally {
      setSavingHomeProfile(false);
    }
  };

  const handleNeedHelp = (clientTask) => {
    const rawTitle = clientTask.task_name || (clientTask.task && clientTask.task.title) || '';
    const title = (catalogTaskCopy(rawTitle, client) || {}).title || rawTitle || 'this task';
    setHelpModal({
      show: true,
      task: clientTask,
      message: `I need help with: ${title}`,
      sending: false,
    });
  };

  const handleSendHelpRequest = async () => {
    const message = helpModal.message.trim();
    if (!helpModal.task || !message) {
      showToast('Please describe what you need help with.', 'warning');
      return;
    }
    setHelpModal({ ...helpModal, sending: true });
    try {
      const result = await homeownerPost('/.netlify/functions/request-task-help', {
        task_id: helpModal.task.id,
        message,
      });
      const notified = Boolean((result.email && result.email.ok) || (result.push && result.push.ok));
      showToast(
        agent
          ? (notified ? 'Your sponsor was notified.' : 'Your request was saved, but your sponsor could not be notified.')
          : 'Your request was saved.',
        notified || !agent ? 'success' : 'warning'
      );
      setHelpModal({ show: false, task: null, message: '', sending: false });
    } catch (error) {
      console.error('Error requesting help:', error);
      showToast(error.message || 'Could not send the help request.', 'error');
      setHelpModal((current) => ({ ...current, sending: false }));
    }
  };

  const clearDocumentDraft = () => {
    setSelectedFile(null);
    setDocumentTitle('');
    setDocumentCategory('');
  };

  const handleUploadDocument = async () => {
    if (readOnlyHome) return false;
    if (!selectedFile) {
      showToast('Please select a file to upload', 'warning');
      return false;
    }
    const title = documentTitleForSave(documentTitle, selectedFile.name);
    if (!title) {
      showToast('Please enter a document title', 'warning');
      return false;
    }
    if (!documentCategory) {
      showToast('Please select a category', 'warning');
      return false;
    }
    const chosen = homeownerDocumentFile(selectedFile);
    if (!chosen.ok) {
      showToast(chosen.message, 'error');
      return false;
    }
    const contentType = chosen.contentType;

    setUploadingDocument(true);
    try {
      const upload = await homeownerRecords({
        resource: 'client_documents',
        action: 'upload_url',
        fileName: selectedFile.name
      });
      const uploadResponse = await fetch(upload.signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: await selectedFile.arrayBuffer()
      });
      if (!uploadResponse.ok) throw new Error(await storageErrorMessage(uploadResponse));

      await homeownerRecords({
        resource: 'client_documents',
        action: 'insert',
        record: {
          title,
          category: documentCategory,
          file_name: selectedFile.name,
          file_path: upload.path,
          file_type: contentType,
          mime_type: contentType,
          file_size: selectedFile.size,
          document_type: documentCategory,
          folder_id: openDocumentFolderId || undefined
        }
      });

      // Success haptic feedback
      try {
        await Haptics.impact({ style: ImpactStyle.Medium });
      } catch (e) {
        devLog('Haptics not supported on this platform');
      }

      showToast('Document uploaded successfully!', 'success');
      clearDocumentDraft();
      await loadDocuments();
      return true;
    } catch (error) {
      console.error('Error uploading document:', error);
      showToast(`Error uploading document: ${error.message || 'Please try again.'}`, 'error');
      return false;
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleViewDocument = async (doc) => {
    try {
      const data = await homeownerRecords({
        resource: 'client_documents',
        action: 'signed_url',
        id: doc.id
      });

      setPreviewDocument({
        ...doc,
        url: data.url
      });
    } catch (error) {
      console.error('Error viewing document:', error);
      showToast('Error viewing document. Please try again.', 'error');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // On native iOS, use signed URL with Capacitor Browser
      const signed = await homeownerRecords({
        resource: 'client_documents',
        action: 'signed_url',
        id: doc.id,
        download: true
      });
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: signed.url });
      } else {
        const fileResponse = await fetch(signed.url);
        if (!fileResponse.ok) throw new Error('Download failed');
        const blob = await fileResponse.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.file_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      showToast('Error downloading document. Please try again.', 'error');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (readOnlyHome) return;
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      // Close preview modal if this document is being previewed
      if (previewDocument && previewDocument.id === docId) {
        setPreviewDocument(null);
      }

      await homeownerRecords({
        resource: 'client_documents',
        action: 'delete',
        id: docId
      });

      await loadDocuments();
      showToast('Document deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting document:', error);
      showToast('Error deleting document. Please try again.', 'error');
    }
  };

  const handleCreateFolder = (name) => runDocumentChange({
    resource: 'client_documents',
    action: 'folder_create',
    name,
  }, 'Could not create that folder.');

  const handleRenameFolder = (folder, name) => runDocumentChange({
    resource: 'client_documents',
    action: 'folder_rename',
    id: folder.id,
    name,
  }, 'Could not rename that folder.');

  const handleDeleteFolder = (folder) => runDocumentChange({
    resource: 'client_documents',
    action: 'folder_delete',
    id: folder.id,
  }, 'Could not delete that folder.');

  const handleShareFolder = (folder, shared) => runDocumentChange({
    resource: 'client_documents',
    action: 'folder_share',
    id: folder.id,
    shared,
  }, 'Could not update sharing.');

  const handleShareDocument = (doc, shared) => runDocumentChange({
    resource: 'client_documents',
    action: 'document_share',
    id: doc.id,
    shared,
  }, 'Could not update sharing.');

  const handleMoveDocument = (doc, folderId) => {
    if (!folderId || folderId === doc.folder_id) return undefined;
    return runDocumentChange({
      resource: 'client_documents',
      action: 'document_move',
      id: doc.id,
      folder_id: folderId,
    }, 'Could not move that document.');
  };

  const reportAssistantError = (message, tone) => {
    setAssistantError(message);
    showToast(message, tone || 'error');
  };

  const handleSendMessage = async (retryMessage) => {
    if (readOnlyHome) return;
    const retry = retryMessage && retryMessage.error ? retryMessage : null;
    const imageFile = retry ? retry.retryImage : selectedImage;
    const imageUrl = retry ? (retry.retryImageUrl || '') : imagePreviewUrl;
    if (!retry && !userInput.trim() && !imageFile) {
      reportAssistantError('Please enter a message or select an image.');
      return;
    }

    const userMessage = retry
      ? retry.retryQuestion
      : (userInput.trim() || (imageFile ? 'Uploaded an image' : ''));
    const priorMessages = messagesInContext(messagesBeforeSend(chatMessages, retry));

    const showFailure = (where, detail) => {
      noteAssistantFailure(where, detail);
      const status = Number(String(detail || '').split(' ')[0]) || 0;
      const serverError = String(detail || '').replace(/^\d+\s*/, '');
      setChatMessages((prev) => prev
        .filter((msg) => !retry || msg.id !== retry.id)
        .concat(assistantErrorMessage({
          where,
          status,
          serverError,
          network: !status,
          question: userMessage,
          image: imageFile,
          imageUrl,
        })));
      if (!retry && imageFile) restoreAssistantAttachment(imageFile, imageUrl);
    };

    if (!retry) {
      setChatMessages((prev) => [...prev, {
        id: `local-user-${Date.now()}`,
        role: 'user',
        content: userMessage,
        image: imageFile && imageFile.name,
        imageUrl,
        timestamp: new Date(),
      }]);
      setUserInput('');
      const field = assistantInputRef.current;
      if (field && typeof field.blur === 'function') field.blur();
      if (imageFile) clearAssistantAttachment();
    } else {
      setChatMessages((prev) => prev.filter((msg) => msg.id !== retry.id));
    }

    setAssistantError('');
    setAiLoading(true);

    try {
      const accessToken = await getHomeownerToken();
      if (!accessToken) {
        showFailure('before-send', '401 not signed in');
        return;
      }
      const conversationHistory = chatHistoryPayload(priorMessages);
      const label = latestApplianceLabel(priorMessages);
      let response;
      let where = 'ask-question';

      if (imageFile) {
        where = 'analyze-appliance';
        const formData = new FormData();
        formData.append('image', await prepareAssistantPhoto(imageFile));
        formData.append('userMessage', userMessage || 'Please identify this appliance and provide maintenance information.');
        formData.append('conversationHistory', JSON.stringify(conversationHistory));
        response = await postAssistant(getApiUrl('/.netlify/functions/analyze-appliance'), {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
          body: formData,
        });
      } else {
        response = await postAssistant(getApiUrl('/.netlify/functions/ask-question'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            question: userMessage,
            conversationHistory,
            label,
          }),
        });
      }

      const payload = await readAssistantJson(response);
      const result = payload.body || {};
      const answer = imageFile ? result.analysis : result.answer;
      if (!payload.ok || result.error || !answer) {
        const detail = result.details ? ` ${result.details}` : '';
        showFailure(where, `${payload.status || 0} ${result.error || 'no answer'}${detail}`);
        return;
      }

      setChatMessages((prev) => [...prev, {
        id: `local-assistant-${Date.now()}`,
        role: 'assistant',
        content: answer,
        label: result.label || null,
        timestamp: new Date(),
        recommendedVendors: result.recommendedVendors || [],
        suggestTask: result.suggestTask || false,
        taskSuggestion: result.taskSuggestion || null,
      }]);
      if (imageFile) clearAssistantAttachment();
      loadConversations();
    } catch (error) {
      showFailure(imageFile ? 'analyze-appliance' : 'ask-question', error && error.message ? error.message : 'network');
    } finally {
      setAiLoading(false);
    }
  };

  const handleTutorialClose = async () => {
    setTourChoiceStored(true);
    setShowTutorial(false);
    try {
      localStorage.setItem(homeownerTourStorageKey(client.id), 'true');
    } catch (error) {
      console.error('Error storing tutorial dismissal:', error);
    }
    try {
      await updateHomeownerClient({
        tutorial_completed: true,
        last_active: new Date().toISOString()
      });
      client.tutorial_completed = true;
      client.last_active = new Date().toISOString();
    } catch (error) {
      console.error('Error saving tutorial completion:', error);
    }
    if (onTourFinished) onTourFinished();
  };

  const homeownerHasPassword = !!(client.has_password || client.password_hash);

  const startHomeownerTour = () => {
    if (showPasswordSetup || !homeownerHasPassword) {
      showToast('Set your password before taking the tour.', 'warning');
      return;
    }
    setShowTutorial(true);
  };

  const openSettingsDestination = (kind) => {
    goToTab('settings');
    window.setTimeout(() => {
      if (kind === 'move') {
        setMorePane(null);
        setShowMove(true);
        return;
      }
      if (kind === 'appearance') {
        setShowMove(false);
        setMorePane(null);
        scrollToDomId('appearance-settings');
        return;
      }
      setShowMove(false);
      setMorePane(kind === 'notifications' || kind === 'sponsor' ? kind : null);
    }, 0);
  };

  const openSearchResult = (result) => {
    if (!result) return;
    if (result.kind === 'task') {
      openRegionalTask({
        taskId: result.id,
        dueDate: result.dueDate,
        title: result.title,
        showAll: true,
      });
      return;
    }
    if (result.kind === 'project') {
      setProjectFilter('all');
      setExpandedProjects((prev) => Object.assign({}, prev, { [result.id]: true }));
      goToTab('projects');
      scrollToDomId(`project-card-${result.id}`);
      return;
    }
    if (result.kind === 'vendor') {
      setVendorTradeFilter('');
      setSelectedVendorCategory('All');
      setVendorSearchTerm('');
      setExpandedVendorId(result.source === 'pro' ? null : result.id);
      setVendorFocusId(result.source === 'pro' ? result.id : null);
      goToTab('vendors');
      scrollToDomId(`vendor-${result.id}`);
      return;
    }
    if (result.kind === 'document') {
      goToTab('documents');
      scrollToDomId(`document-${result.id}`);
      return;
    }
    if (result.kind === 'detail') {
      goToTab(result.tab || 'property');
      scrollToDomId(result.systemId ? 'home-detail-facts' : result.anchor);
      return;
    }
    if (result.kind !== 'place') return;
    if (result.place === 'legal') {
      openLegalPath(LEGAL_INDEX_PATH);
      return;
    }
    if (result.place === 'replay-tour') {
      startHomeownerTour();
      return;
    }
    if (result.pane || result.place === 'move' || result.place === 'appearance') {
      openSettingsDestination(result.pane || result.place);
      return;
    }
    if (result.tab) goToTab(result.tab);
  };

  // Filter tasks
  const upcomingTasks = tasks.filter(t => {
    if (!t.due_date || t.completed) return false;

    const dueDate = parseLocalDate(t.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day

    const thirtyDaysOut = new Date();
    thirtyDaysOut.setHours(0, 0, 0, 0);
    thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30);

    return dueDate >= today && dueDate <= thirtyDaysOut;
  }).sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    const aPriority = priorityOrder[a.task?.priority] || 3;
    const bPriority = priorityOrder[b.task?.priority] || 3;
    if (aPriority !== bPriority) return aPriority - bPriority;
    return dateOnlyKey(a.due_date).localeCompare(dateOnlyKey(b.due_date));
  });

  const overdueTasks = tasks.filter(t => {
    if (!t.due_date || t.completed) return false;

    const dueDate = parseLocalDate(t.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day

    return dueDate < today;
  });

  const brandColor = PACARION_BRAND_COLOR;
  const canWriteProjects = !readOnlyHome && Boolean(homePlan && homePlan.entitled);
  // Active tab label and underline. #59c4c4 on white is about 2.1:1.
  // #0f766e is about 5.5:1. Dark mode keeps the brand teal.
  const activeTabColor = isDark ? brandColor : '#0f766e';
  const homeownerSectionHeading = {
    ...styles.sectionTitle,
    color: colors.text,
    marginTop: 0,
    marginBottom: '0.75rem',
    fontWeight: 300,
  };

  if (loading) {
    return (
      <div className="app-scroll" style={{
        ...styles.container,
        background: colors.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: `4px solid ${colors.border}`,
            borderTop: `4px solid ${brandColor}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1.5rem'
          }}></div>
          <div style={{
            fontSize: '1.1rem',
            color: colors.text,
            fontWeight: 500
          }}>Loading your home dashboard...</div>
          <div style={{
            fontSize: '0.9rem',
            color: colors.textSecondary,
            marginTop: '0.5rem'
          }}>Please wait</div>
        </div>
      </div>
    );
  }

  const shownAgentVendors = withoutAdoptedCopies(vendors, homeownerPros);
  const matchVendorsToProjectTask = (task) => matchProjectTaskVendors(task, shownAgentVendors);

  // Render function, not a component declared in this function. A nested
  // component is a new type every render, so toggling remounts the button
  // and keyboard focus jumps to the top of the page.
  const renderTaskCard = (t) => {
    const isExpanded = expandedTaskId === t.id;
    const rawTitle = t.task_name || (t.task && t.task.title) || '';
    const genericCopy = catalogTaskCopy(rawTitle, client);
    const displayTitle = plainTaskTitle((genericCopy && genericCopy.title) || rawTitle || 'Untitled Task');
    const filterLines = isAcFilterTask(t) || isAcFilterTask({ title: displayTitle }) ? acFilterDescription(client) : '';
    const displayDescription = displayTaskDescription(
      (genericCopy && genericCopy.description)
      || (t.task && t.task.description)
      || t.description
      || floridaTaskDescription(rawTitle)
      || 'No description available.',
      client
    );
    const sponsorVendor = sponsorVendorForTask(t, shownAgentVendors);
    const effort = effortLabel(t);

    // Light-mode backgrounds stay the pale status colors. In dark mode those
    // same creams sat under near-white title text, so overdue / due-soon cards
    // use dark fills and the title (colors.text) stays readable.
    const getTaskStatusColor = () => {
      if (t.completed) {
        return { bg: colors.surface, border: '#10b981', accent: '#10b981' };
      }
      if (t.due_date) {
        const dueDate = parseLocalDate(t.due_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffDays = dueDate ? Math.round((dueDate - today) / (1000 * 60 * 60 * 24)) : 0;

        if (diffDays < 0) {
          return isDark
            ? { bg: '#3f1d1d', border: '#991b1b', accent: '#fca5a5' }
            : { bg: '#fef2f2', border: '#fca5a5', accent: '#ef4444' };
        } else if (diffDays <= 3) {
          return isDark
            ? { bg: '#3b2a12', border: '#92400e', accent: '#fbbf24' }
            : { bg: '#fffbeb', border: '#fcd34d', accent: '#f59e0b' };
        }
      }
      return { bg: colors.surface, border: colors.border, accent: colors.text };
    };

    const priorityBadge = (priority) => {
      if (!isDark) {
        // #59c4c4 on #e0f5f5 is about 1.8:1. #115e59 is about 6.7:1.
        // #f59e0b on #fef3c7 is about 1.9:1. #92400e is about 6.4:1.
        if (priority === 'high') return { background: '#e0f5f5', color: '#115e59' };
        if (priority === 'medium') return { background: '#fef3c7', color: '#92400e' };
        return { background: '#f3f4f6', color: '#6b7280' };
      }
      if (priority === 'high') return { background: '#134e4a', color: '#ccfbf1' };
      if (priority === 'medium') return { background: '#78350f', color: '#fde68a' };
      return { background: '#374151', color: '#e5e7eb' };
    };

    const statusColor = getTaskStatusColor();
    const panelId = `task-details-${t.id}`;
    const overdue = !t.completed && isBeforeDateOnly(t.due_date, new Date());
    const snoozed = !t.completed && t.snoozed_until && new Date(t.snoozed_until) > new Date();
    const priorityLabel = t.task && t.task.priority ? String(t.task.priority).toUpperCase() : '';
    const categoryLabel = t.task && t.task.category ? t.task.category : '';
    const tagLabel = priorityLabel || categoryLabel;
    const tagStyle = priorityLabel ? priorityBadge(t.task.priority) : { background: isDark ? '#374151' : '#f3f4f6', color: isDark ? '#e5e7eb' : '#4b5563' };
    const quietBtn = {
      ...styles.modernBtn,
      background: 'transparent',
      color: colors.text,
      border: `1px solid ${colors.border}`,
      boxShadow: 'none',
    };
    const taskImage = (t.task && t.task.image_url) || '';

    return (
      <div key={t.id} id={`task-${t.id}`} style={{
        ...styles.taskCard,
        padding: '1rem 1.05rem',
        borderRadius: '1rem',
        background: statusColor.bg,
        border: `1px solid ${statusColor.border}`,
        borderLeft: `4px solid ${statusColor.accent}`
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <input
            type="checkbox"
            aria-label={`Mark ${displayTitle} done`}
            checked={t.completed}
            disabled={readOnlyHome}
            onChange={() => handleCompleteTask(t.id, t.completed)}
            style={{ ...styles.checkbox, marginTop: '0.2rem' }}
          />
          <button
            type="button"
            className="task-card-toggle"
            aria-expanded={isExpanded}
            aria-controls={panelId}
            onClick={() => setExpandedTaskId(isExpanded ? null : t.id)}
            style={{
              flex: '1 1 auto',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.5rem',
              minWidth: 0,
              width: 'auto',
              margin: 0,
              padding: '0.15rem 0',
              background: 'transparent',
              border: 'none',
              boxShadow: 'none',
              color: colors.text,
              textAlign: 'left',
              cursor: 'pointer',
              minHeight: '44px',
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.35, color: colors.text }}>
                {displayTitle}
              </span>
              <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center', marginTop: '0.35rem' }}>
                {t.due_date ? (
                  <span style={{ fontSize: '0.85rem', color: overdue ? (isDark ? '#fecaca' : '#991b1b') : colors.textSecondary }}>
                    Due {formatDateOnly(t.due_date)}
                  </span>
                ) : null}
                {tagLabel ? (
                  <span style={{ ...styles.badge, ...tagStyle, padding: '0.12rem 0.45rem', fontSize: '0.68rem' }}>{tagLabel}</span>
                ) : null}
                <span style={{ fontSize: '0.85rem', color: colors.textSecondary }}>{effort}</span>
                {t.completion_photo_path ? <TaskPhotoThumb path={t.completion_photo_path} /> : null}
                {snoozed ? (
                  <span style={{
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: isDark ? '#dbeafe' : '#1e3a5f',
                    background: isDark ? '#1e3a5f' : '#dbeafe',
                    borderRadius: '999px',
                    padding: '0.12rem 0.45rem',
                  }}>Snoozed</span>
                ) : null}
              </span>
            </span>
            <span aria-hidden="true" style={{ color: isDark ? colors.textMuted : colors.textSecondary, fontSize: '1rem', lineHeight: 1, paddingTop: '0.2rem' }}>
              {isExpanded ? '\u25B4' : '\u25BE'}
            </span>
          </button>
        </div>

        {isExpanded && (
          <div id={panelId} style={{ marginTop: '0.9rem' }}>
            {/^https?:\/\//i.test(String(taskImage)) && (
              <img
                src={taskImage}
                alt=""
                style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', borderRadius: '0.75rem', marginBottom: '0.75rem' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div style={{ padding: '0.9rem', background: colors.surfaceAlt, borderRadius: '0.75rem' }}>
            <h4 style={{margin: '0 0 0.5rem 0', fontSize: '1rem', color: colors.text}}>Task Details</h4>
            <p style={{margin: '0.5rem 0', color: colors.textSecondary}}>
              {taskSummaryText(filterLines ? String(displayDescription).split('\n\nAC 1:')[0] : displayDescription)}
            </p>
            {filterLines ? (
              <p style={{ margin: '0.5rem 0', color: colors.textSecondary, whiteSpace: 'pre-wrap' }}>{filterLines}</p>
            ) : null}

            <TaskHelpCard
              task={t}
              home={client}
              vendorName={sponsorVendor ? sponsorVendor.name : ''}
              colors={colors}
              readOnly={readOnlyHome}
              onHire={sponsorVendor ? () => openFindAPro(tradeForTaskCategory((t.task && t.task.category) || t.category) || '') : null}
              onPhoto={(file) => {
                if (!file) {
                  showToast('Could not open photos.', 'error');
                  return;
                }
                handleCompletionPhoto(t, file);
              }}
            />
            {t.task?.youtube_video_url && getYouTubeThumbnail(t.task.youtube_video_url) && (
                  <div style={{marginBottom: '1rem'}}>
                    <div style={{
                      position: 'relative',
                      cursor: 'pointer',
                      borderRadius: isMobile ? '1.25rem' : '0.5rem',
                      overflow: 'hidden',
                      border: `2px solid ${brandColor}`
                    }}
                    onClick={() => openExternalUrl(t.task.youtube_video_url)}
                    >
                      <img
                        src={getYouTubeThumbnail(t.task.youtube_video_url)}
                        alt="Video tutorial"
                        style={{width: '100%', display: 'block'}}
                      />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(255, 0, 0, 0.8)',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{
                          width: 0,
                          height: 0,
                          borderLeft: '20px solid white',
                          borderTop: '12px solid transparent',
                          borderBottom: '12px solid transparent',
                          marginLeft: '5px'
                        }} />
                      </div>
                    </div>
                    <p style={{fontSize: '0.85rem', color: colors.textSecondary, marginTop: '0.5rem', textAlign: 'center'}}>
                      Click to watch tutorial video
                    </p>
                  </div>
                )}
            </div>

            <div style={{ marginTop: '0.9rem' }}>
              <h4 style={{ margin: '0 0 0.4rem 0', fontSize: '0.95rem', color: colors.text }}>Notes</h4>
              {readOnlyHome ? (
                <p style={{ margin: 0, fontSize: '0.9rem', color: t.client_notes ? colors.textSecondary : colors.textMuted, fontStyle: t.client_notes ? 'italic' : 'normal' }}>
                  {t.client_notes || 'No notes yet.'}
                </p>
              ) : editingTaskNote === t.id ? (
                <div>
                  <textarea
                    value={taskNoteText}
                    onChange={(e) => setTaskNoteText(e.target.value)}
                    style={{ ...styles.input, minHeight: '60px', background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text }}
                    placeholder="Add your notes..."
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => handleSaveTaskNote(t.id)} style={styles.smallBtn}>Save Note</button>
                    <button type="button" onClick={() => { setEditingTaskNote(null); setTaskNoteText(''); }} style={quietBtn}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div>
                  {t.client_notes ? (
                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: colors.textSecondary, fontStyle: 'italic' }}>{t.client_notes}</p>
                  ) : (
                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: colors.textMuted }}>No notes yet.</p>
                  )}
                  <button
                    type="button"
                    onClick={() => { setEditingTaskNote(t.id); setTaskNoteText(t.client_notes || ''); }}
                    style={quietBtn}
                  >
                    Add Note
                  </button>
                </div>
              )}
            </div>

            {!readOnlyHome && !t.completed && (
              <div style={{ marginTop: '0.9rem' }}>
                {pushTaskId === t.id ? (
                  <div id={`already-done-${t.id}`}>
                    <label style={{ display: 'block', fontWeight: 650, marginBottom: '0.35rem', color: colors.text }}>When did you do it?</label>
                    <input
                      type="date"
                      value={pushDate}
                      onChange={(event) => setPushDate(event.target.value)}
                      style={{ width: '100%', minHeight: '48px', fontSize: '1rem', marginBottom: '0.5rem', boxSizing: 'border-box' }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button type="button" disabled={pushing || !pushDate} onClick={() => handlePushOut(t.id)} style={{ ...styles.smallBtn, minHeight: '44px' }}>
                        {pushing ? 'Saving...' : 'Mark done on this date'}
                      </button>
                      <button type="button" onClick={() => setPushTaskId(null)} style={{ ...styles.smallBtn, minHeight: '44px' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                      <button type="button" style={styles.modernBtn} onClick={() => setSnoozeModal({ show: true, taskId: t.id, date: '' })}>Snooze</button>
                      <button type="button" style={quietBtn} onClick={() => setDismissModal({ show: true, taskId: t.id, reason: '' })}>Not for my home</button>
                      <button
                        type="button"
                        style={{ ...styles.modernBtn, background: isDark ? '#c2410c' : '#f59e0b' }}
                        onClick={() => setRescheduleModal({ show: true, taskId: t.id, date: dateOnlyKey(t.due_date) || '' })}
                      >
                        Reschedule
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => { setPushTaskId(t.id); setPushDate(dateOnlyKey(new Date())); }}
                        style={quietBtn}
                      >
                        Already done recently?
                      </button>
                      <button type="button" disabled={pushing} onClick={() => handleSkipYear(t.id)} style={quietBtn}>Skip this year</button>
                      <button type="button" style={quietBtn} onClick={() => setReminderModal({ show: true, taskId: t.id, date: t.due_date })}>Set Reminder</button>
                      <button type="button" style={{ ...quietBtn, borderColor: isDark ? '#c2410c' : '#f59e0b', color: isDark ? '#fdba74' : '#9a3412' }} onClick={() => handleNeedHelp(t)}>Need Help?</button>
                    </div>
                  </>
                )}
              </div>
            )}
            {!readOnlyHome && t.completed && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.9rem' }}>
                <button type="button" style={styles.modernBtn} onClick={() => setSnoozeModal({ show: true, taskId: t.id, date: '' })}>Snooze</button>
                <button type="button" style={{ ...styles.modernBtn, background: isDark ? '#c2410c' : '#f59e0b' }} onClick={() => setRescheduleModal({ show: true, taskId: t.id, date: dateOnlyKey(t.due_date) || '' })}>Reschedule</button>
                <button type="button" style={quietBtn} onClick={() => setReminderModal({ show: true, taskId: t.id, date: t.due_date })}>Set Reminder</button>
                <button type="button" style={{ ...quietBtn, borderColor: isDark ? '#c2410c' : '#f59e0b', color: isDark ? '#fdba74' : '#9a3412' }} onClick={() => handleNeedHelp(t)}>Need Help?</button>
              </div>
            )}
            {!readOnlyHome ? (
              <div style={{ marginTop: '0.9rem' }}>
                {ownTaskEdit && ownTaskEdit.id === t.id ? (
                  <div>
                    <label style={{ display: 'block', fontWeight: 650, marginBottom: '0.35rem', color: colors.text }}>Task title</label>
                    <input
                      type="text"
                      value={ownTaskEdit.title}
                      onChange={(event) => setOwnTaskEdit(Object.assign({}, ownTaskEdit, { title: event.target.value }))}
                      style={{ width: '100%', minHeight: '48px', fontSize: '1rem', marginBottom: '0.5rem', boxSizing: 'border-box' }}
                    />
                    <label style={{ display: 'block', fontWeight: 650, marginBottom: '0.35rem', color: colors.text }}>Due date</label>
                    <input
                      type="date"
                      value={ownTaskEdit.due_date}
                      onChange={(event) => setOwnTaskEdit(Object.assign({}, ownTaskEdit, { due_date: event.target.value }))}
                      style={{ width: '100%', minHeight: '48px', fontSize: '1rem', marginBottom: '0.5rem', boxSizing: 'border-box' }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button type="button" onClick={handleSaveOwnTask} style={{ ...styles.smallBtn, minHeight: '44px' }}>Save</button>
                      <button type="button" onClick={() => setOwnTaskEdit(null)} style={{ ...styles.smallBtn, minHeight: '44px' }}>Cancel</button>
                    </div>
                  </div>
                ) : ownTaskDeleteId === t.id ? (
                  <div>
                    <p style={{ margin: '0 0 0.5rem', color: colors.text }}>Delete this task?</p>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => handleDeleteOwnTask(t.id)} style={{ ...styles.smallBtn, minHeight: '44px' }}>Delete</button>
                      <button type="button" onClick={() => setOwnTaskDeleteId(null)} style={{ ...styles.smallBtn, minHeight: '44px' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      style={quietBtn}
                      onClick={() => setOwnTaskEdit({ id: t.id, title: rawTitle, due_date: dateOnlyKey(t.due_date) || '' })}
                    >
                      Edit
                    </button>
                    <button type="button" style={quietBtn} onClick={() => setOwnTaskDeleteId(t.id)}>Delete</button>
                    {!t.completed ? (
                      <button type="button" style={styles.modernBtn} onClick={() => handleCompleteTask(t.id, false)}>Mark complete</button>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  const openManagePlan = async () => {
    if (isNativeApp()) return;
    try {
      const result = await startHomeownerCheckout({ action: 'portal' });
      if (result && result.url) window.location.assign(result.url);
    } catch (error) {
      showToast((error && error.message) || 'Could not open billing.', 'info');
    }
  };

  const sponsorless = !agent;
  const sponsorVendorTitle = sponsorVendorHeading({
    agentName: [agent?.first_name, agent?.last_name].filter(Boolean).join(' '),
    subject: agent?.provider_list_subject,
    orgName: agent?.team_name || agent?.brokerage,
  });
  const propertyTypeLabel = (propertyTypes.find((type) => type.id === client.property_type) || {}).label || 'Single Family Home';
  const searchCatalog = buildHomeSearchCatalog({
    tasks,
    projects,
    vendors: shownAgentVendors,
    pros: homeownerPros,
    includeAgentVendors: !sponsorless,
    documents,
    client,
    countyName: countyLabel(client.property_county_fips),
    propertyTypeLabel,
    features: availableFeatures.filter((feature) => featureSelected(propertyData.features, feature.id)),
    systems: getMergedSystems(),
    contacts: propertyContacts,
    agent,
    sponsorless,
    readOnly: readOnlyHome,
  });
  const navItems = homeownerTabs(HOMEOWNER_DESKTOP_TAB_IDS).map((item) => (
    sponsorless && item.id === 'agent' ? { id: item.id, label: 'Pacarion' } : item
  ));
  const moreLinks = homeownerTabs(HOMEOWNER_MORE_LINK_IDS).map((item) => (
    sponsorless && item.id === 'agent' ? { id: item.id, label: 'Pacarion' } : item
  ));

  return (
    <>
      <AnnouncementBanner audience="homeowner" />
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes buttonBounce {
          0% { transform: scale(1); }
          50% { transform: scale(0.95); }
          100% { transform: scale(1); }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        button:not(.ios-tab-item):not(.task-card-toggle):not(.doc-upload-toggle):not(.doc-share-switch):not(.account-push-switch):not(.doc-folder-row):not(.doc-back-row) {
          min-height: 44px; /* iOS touch target minimum */
          min-width: 44px; /* iOS touch target minimum */
          transform: none;
        }
        button:active:not(.ios-tab-item):not(.app-menu-row):not(.task-card-toggle):not(.plan-unsaved-hint):not(.doc-upload-toggle):not(.doc-share-switch):not(.account-push-switch):not(.doc-folder-row):not(.doc-back-row) {
          transform: none;
        }

        /* Touch target size for controls. Anchors stay in normal flow so a
           link inside a sentence cannot force the page wider than the screen. */
        button:not(.ios-tab-item):not(.task-card-toggle):not(.doc-share-switch):not(.account-push-switch), .clickable, [role="button"]:not(.ios-tab-item) {
          min-height: 44px;
          min-width: 44px;
        }
        input:focus, textarea:focus, select:focus {
          outline: none;
          border-color: #59c4c4 !important;
          box-shadow: 0 0 0 2px rgba(89, 196, 196, 0.1) !important;
        }

        /* iOS-specific styles */
        :root {
          --safe-area-inset-top: env(safe-area-inset-top);
          --safe-area-inset-bottom: env(safe-area-inset-bottom);
          --safe-area-inset-left: env(safe-area-inset-left);
          --safe-area-inset-right: env(safe-area-inset-right);
        }

        /* Mobile content padding to account for bottom tab bar */
        @media (max-width: 768px) {
          .mobile-content {
            padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px)) !important;
          }
        }
      `}</style>
      <div className={`app-shell${activeTab === 'assistant' ? ' assistant-open' : ''}`} style={{...styles.container, background: colors.background, maxWidth: '100%', overflowX: 'clip'}}>
        {showPasswordSetup && (
        <ModalLayer className="app-modal-layer" style={{...styles.modal, zIndex: 12000}}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}}>
            <h2 style={{color: colors.text}}>{client.password_reset_required ? 'Reset your password' : 'Set Up Your Account'}</h2>
            <p style={{color: colors.textSecondary, marginBottom: '1.5rem'}}>
              {client.password_reset_required
                ? 'The saved password is not usable. Choose a new one to continue.'
                : <>Create a password: <strong>{client.email}</strong></>}
            </p>
            <form onSubmit={handlePasswordSetup}>
              <input
                type="password"
                placeholder="New Password (min 8 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{...styles.input, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text}}
                minLength={8}
                required
              />
              <input
                type="password"
                placeholder="New password again"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{...styles.input, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text}}
                required
              />
              <button type="submit" style={styles.button}>
                Set Password
              </button>
            </form>
          </div>
        </ModalLayer>
      )}

      {snoozeModal.show && (
        <ModalLayer className="app-modal-layer" style={styles.modal}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}}>
            <h2 style={{color: colors.text}}>Snooze</h2>
            <p style={{color: colors.textSecondary, marginBottom: '1rem'}}>Move this chore out of the way.</p>
            <div style={{display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap'}}>
              <button type="button" onClick={() => handleSnoozeDays(7)} style={styles.button}>1 week</button>
              <button type="button" onClick={() => handleSnoozeDays(30)} style={styles.button}>1 month</button>
              <button type="button" onClick={() => setSnoozeModal({ show: false, taskId: null, date: '' })} style={styles.button}>Cancel</button>
            </div>
          </div>
        </ModalLayer>
      )}

      {dismissModal.show && (
        <ModalLayer className="app-modal-layer" style={styles.modal}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}}>
            <h2 style={{color: colors.text}}>Not for my home</h2>
            <p style={{color: colors.textSecondary, marginBottom: '1rem'}}>This chore stays hidden. A short reason helps tune the list.</p>
            <textarea
              aria-label="Why this is not for your home"
              value={dismissModal.reason}
              onChange={(event) => setDismissModal({ ...dismissModal, reason: event.target.value })}
              style={{ ...styles.input, minHeight: 80, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <button type="button" onClick={handleNotForHome} style={styles.button}>Hide this chore</button>
              <button type="button" onClick={() => setDismissModal({ show: false, taskId: null, reason: '' })} style={styles.button}>Cancel</button>
            </div>
          </div>
        </ModalLayer>
      )}

      {rescheduleModal.show && (
        <ModalLayer className="app-modal-layer" style={styles.modal}>
          <div className="app-modal-card" role="dialog" aria-labelledby="reschedule-task-title" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}}>
            <h2 id="reschedule-task-title" style={{color: colors.text}}>Reschedule Task</h2>
            <p style={{color: colors.textSecondary, marginBottom: '1rem'}}>New due date:</p>
            <input
              type="date"
              aria-label="New due date"
              value={rescheduleModal.date}
              onChange={(e) => setRescheduleModal({...rescheduleModal, date: e.target.value})}
              style={{...styles.input, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text}}
            />
            <div style={{display: 'flex', gap: '0.5rem', marginTop: '1rem'}}>
              <button type="button" onClick={handleRescheduleTask} style={styles.button}>Save date</button>
              <button type="button" onClick={() => setRescheduleModal({ show: false, taskId: null, date: '' })} style={styles.button}>Cancel</button>
            </div>
          </div>
        </ModalLayer>
      )}

      {reminderModal.show && (
        <ModalLayer className="app-modal-layer" style={styles.modal}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}}>
            <h2 style={{color: colors.text}}>Set Reminder</h2>
            <p style={{color: colors.textSecondary, marginBottom: '1rem'}}>Remind me on:</p>
            <input
              type="date"
              value={reminderModal.date}
              onChange={(e) => setReminderModal({...reminderModal, date: e.target.value})}
              style={{...styles.input, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text}}
            />
            <div style={{display: 'flex', gap: '0.5rem', marginTop: '1rem'}}>
              <button
                onClick={handleSetReminder}
                style={styles.button}
              >
                Set Reminder
              </button>
              <button
                onClick={() => setReminderModal({ show: false, taskId: null, date: '' })}
                style={styles.button}
              >
                Cancel
              </button>
            </div>
          </div>
        </ModalLayer>
      )}

      {showAddTaskModal && (
        <ModalLayer className="app-modal-layer" style={styles.modal}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`, maxWidth: '500px'}}>
            <h2 style={{color: colors.text, marginBottom: '1.5rem'}}>Add New Task</h2>

            <div style={styles.formGroup}>
              <label style={{...styles.label, color: colors.text}}>Task Title *</label>
              <input
                type="text"
                value={newTaskData.title}
                onChange={(e) => setNewTaskData({...newTaskData, title: e.target.value})}
                style={{...styles.input, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text}}
                placeholder="e.g., Change HVAC filter"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={{...styles.label, color: colors.text}}>Due Date *</label>
              <input
                type="date"
                value={newTaskData.due_date}
                onChange={(e) => setNewTaskData({...newTaskData, due_date: e.target.value})}
                style={{
                  ...styles.input,
                  display: 'block',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  background: colors.surface,
                  border: `2px solid ${brandColor}`,
                  color: colors.text,
                }}
                min={localDateString(new Date())}
              />
            </div>

            <div style={{display: 'flex', gap: '0.5rem', marginTop: '1.5rem'}}>
              <button
                onClick={handleAddTask}
                style={{
                  ...styles.button,
                  background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
                  boxShadow: '0 2px 8px rgba(89, 196, 196, 0.3)'
                }}
              >
                Add Task
              </button>
              <button
                onClick={() => {
                  setShowAddTaskModal(false);
                  setNewTaskData({
                    title: '',
                    due_date: ''
                  });
                }}
                style={styles.button}
              >
                Cancel
              </button>
            </div>
          </div>
        </ModalLayer>
      )}

      {helpModal.show && (
        <ModalLayer className="app-modal-layer" style={styles.modal}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`, maxWidth: '500px'}}>
            <h2 style={{color: colors.text}}>Need Help?</h2>
            <p style={{color: colors.textSecondary, marginBottom: '1rem'}}>
              {agent
                ? 'This sends a request to your sponsor. They get a notification and an email.'
                : 'This request is saved with your account.'}
            </p>
            <textarea
              value={helpModal.message}
              onChange={(e) => setHelpModal({ ...helpModal, message: e.target.value })}
              style={{...styles.input, minHeight: '120px', background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text}}
              maxLength={2000}
              placeholder="Describe what you need help with"
            />
            <div style={{display: 'flex', gap: '0.5rem', marginTop: '1rem'}}>
              <button
                onClick={handleSendHelpRequest}
                disabled={helpModal.sending}
                style={styles.button}
              >
                {helpModal.sending ? 'Sending...' : (agent ? 'Send to sponsor' : 'Save request')}
              </button>
              <button
                onClick={() => setHelpModal({ show: false, task: null, message: '', sending: false })}
                style={styles.button}
              >
                Cancel
              </button>
            </div>
          </div>
        </ModalLayer>
      )}

      {previewDocument && (
        <ModalLayer className="app-modal-layer" style={styles.modal}>
          <div className="app-modal-card" style={{...styles.modalContent, maxWidth: '100%', width: '100%', background: colors.surface, border: `1px solid ${colors.border}`}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
              <h2 style={{margin: 0, color: colors.text}}>{previewDocument.title}</h2>
              <button
                onClick={() => setPreviewDocument(null)}
                style={styles.smallBtn}
              >
                Close
              </button>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              maxHeight: '70vh',
              overflow: 'auto'
            }}>
              {previewDocument.mime_type?.includes('pdf') ? (
                <iframe
                  src={previewDocument.url}
                  style={{
                    width: '100%',
                    height: '70vh',
                    border: 'none',
                    borderRadius: '0.5rem'
                  }}
                  title={previewDocument.title}
                />
              ) : previewDocument.mime_type?.includes('image') ? (
                <img
                  src={previewDocument.url}
                  alt={previewDocument.title}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: '0.5rem'
                  }}
                />
              ) : (
                <div style={{textAlign: 'center', padding: '2rem'}}>
                  <p style={{marginBottom: '1rem', color: colors.textSecondary}}>
                    Preview not available for this file type.
                  </p>
                  <a
                    href={previewDocument.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: brandColor,
                      textDecoration: 'underline',
                      fontSize: '1rem'
                    }}
                  >
                    Open in New Tab
                  </a>
                </div>
              )}
            </div>
          </div>
        </ModalLayer>
      )}

      {/* First sign-in starts the tour. Replay tour in Settings opens it again. */}
      {showTutorial && !showPasswordSetup && (
        <ClientTutorial
          onClose={handleTutorialClose}
          onShowStep={(step) => {
            if (step && step.section) goToTab(step.section);
          }}
        />
      )}

      {/* iOS Bottom Tab Bar - Mobile Only */}
      {isMobile && (
        <AppTabBar
          tabs={HOMEOWNER_MOBILE_TABS}
          activeId={activeTab}
          onSelect={goToTab}
          colors={colors}
          isDark={isDark}
          badgeFor={(tab) => (
            tab.id === 'projects'
              ? projectTabBadgeCount(incompleteTaskCount, seenProjectCount, activeTab === 'projects')
              : 0
          )}
        />
      )}

      <header
        className={isMobile ? 'mobile-top-bar' : undefined}
        style={{
        ...styles.header,
        background: colors.surface,
        color: colors.text,
        borderBottom: `1px solid ${colors.border}`,
        boxSizing: 'border-box',
        width: isMobile ? '100%' : undefined,
        maxWidth: '100%',
        padding: isMobile ? undefined : '1.5rem 3rem',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : undefined,
        paddingBottom: isMobile ? 0 : undefined,
        paddingLeft: isMobile ? 'calc(env(safe-area-inset-left, 0px) + 16px)' : undefined,
        paddingRight: isMobile ? 'calc(env(safe-area-inset-right, 0px) + 16px)' : undefined,
        minHeight: isMobile ? 'calc(env(safe-area-inset-top, 0px) + var(--mobile-top-bar-height, 44px))' : undefined,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        position: isMobile ? 'fixed' : 'relative',
        top: isMobile ? 0 : 'auto',
        left: isMobile ? 0 : 'auto',
        right: isMobile ? 0 : 'auto',
        zIndex: showTutorial ? 10050 : (isMobile ? 999 : 2),
        backgroundColor: colors.surface,
        transform: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        gap: isMobile ? '0.75rem' : '2rem',
        overflow: isMobile ? 'hidden' : undefined
      }}>
        <img
          className="top-bar-wordmark"
          src={isDark ? `${process.env.PUBLIC_URL}/images/logo/pacarion-logo-dark.png` : `${process.env.PUBLIC_URL}/images/logo/pacarion-logo-light.png`}
          alt="Pacarion"
          style={{height: isMobile ? '33px' : '40px', width: 'auto', maxWidth: '70%', minWidth: 0, flexShrink: 1}}
          onError={(e) => e.target.style.display = 'none'}
        />
        {/* Header stays above the tour overlay so Logout remains clickable. */}
        {(!isMobile || showTutorial) && (
        <div style={{display: 'flex', gap: '0.75rem', alignItems: 'center'}}>
          <button style={{...styles.logoutBtn, color: colors.text, borderColor: colors.borderLight}} onClick={onLogout}>Logout</button>
        </div>
        )}
      </header>

      {/* Desktop-only Tab Bar */}
      {!isMobile && (
        <div style={{...styles.tabContainer, background: colors.surface, borderBottom: `1px solid ${colors.border}`}}>
          <div style={{ position: 'relative', width: '100%', maxWidth: '100%', minWidth: 0 }}>
            <div className="homeowner-desktop-tabs" style={styles.tabs}>
              {navItems.map(item => (
                <button
                  type="button"
                  key={item.id}
                  data-tour={item.id}
                  style={{
                    ...styles.tab,
                    color: activeTab === item.id ? activeTabColor : colors.textSecondary,
                    ...(activeTab === item.id ? {...styles.tabActive, borderBottomColor: activeTabColor, color: activeTabColor} : {})
                  }}
                  onClick={() => goToTab(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div
        className={`app-scroll${isMobile ? ' mobile-content mobile-under-header' : ''}${activeTab === 'assistant' ? ' assistant-pane' : ''}`}
        data-pacarion-build="header-legal-2"
        style={{
          ...styles.main,
          boxSizing: 'border-box',
          width: '100%',
          maxWidth: isMobile ? '100%' : '1400px',
          minWidth: 0,
          padding: isMobile ? '1rem' : '4rem 3rem',
          paddingTop: isMobile ? 'calc(env(safe-area-inset-top, 0px) + var(--mobile-top-bar-height, 44px) + var(--page-below-bar-gap, 16px))' : '4rem',
          paddingLeft: isMobile ? 'calc(env(safe-area-inset-left, 0px) + 16px)' : '3rem',
          paddingRight: isMobile ? 'calc(env(safe-area-inset-right, 0px) + 16px)' : '3rem',
          minHeight: isMobile ? '100%' : 'auto',
          overflowX: isMobile ? 'hidden' : 'visible',
          overflowY: 'visible'
        }}
      >
        {readOnlyHome && (
          <ArchivedHomeBanner
            colors={colors}
            brandColor={brandColor}
            busy={switchingHome}
            onBack={async () => {
              setSwitchingHome(true);
              try {
                const listed = await listHomeownerHomes();
                const homes = (listed && listed.homes) || [];
                let current = null;
                for (let index = 0; index < homes.length; index += 1) {
                  if (homes[index] && !homes[index].readOnly) current = homes[index];
                }
                if (!current) {
                  showToast('No current home is available.', 'info');
                  return;
                }
                await switchHomeownerHome(current.id);
                const loaded = await loadHomeownerClient();
                if (onClientReplaced) onClientReplaced(loaded.client || loaded);
              } catch (error) {
                showToast('Could not switch homes.', 'error');
              } finally {
                setSwitchingHome(false);
              }
            }}
          />
        )}
        {pushUndo && activeTab === pushUndoTab && (
          <div id="task-undo" style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            marginBottom: '1rem',
            padding: '0.85rem 1rem',
            borderRadius: '0.75rem',
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            gap: '0.75rem',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}>
            <span style={{ color: colors.text }}>Marked as already done.</span>
            <button type="button" onClick={handleUndoPush} style={{ ...styles.smallBtn, minHeight: '44px' }}>
              Undo
            </button>
          </div>
        )}
        {activeTab === 'overview' && (
          <div data-tour="overview">
            {!readOnlyHome && homeSetupFinished(client) && !homeFactsCardHidden && !homeFactsInviteDone(client) ? (
              <HomeFactsInviteCard
                onOpen={() => {
                  goToTab('property');
                  window.setTimeout(() => {
                    const node = document.getElementById('home-detail-facts');
                    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'start' });
                  }, 0);
                }}
                onDismiss={() => {
                  dismissHomeFactsCard(window.localStorage, client.id);
                  setHomeFactsCardHidden(true);
                }}
              />
            ) : null}
            <div data-tour="home-search">
              <HomeSearchBar
                catalog={searchCatalog}
                homeId={client.id}
                colors={colors}
                compact={isMobile || isNativeApp()}
                onSelect={openSearchResult}
              />
            </div>
            {joinRequest && (joinRequest.status === 'pending' || joinRequest.status === 'declined') ? (
              <FriendStatus
                status={joinRequest.status}
                agentFirstName={joinRequest.agentFirstName}
                onStartOwn={() => {
                  if (isNativeApp()) return;
                  window.history.pushState({}, '', '/homeowner/plan');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
              />
            ) : (sponsorless ? <HomeownerPlanBanner /> : null)}
            {showTasksReady ? (
              <div
                role="status"
                data-testid="tasks-ready-note"
                style={{
                  margin: '0 0 1rem',
                  padding: '0.9rem 1rem',
                  borderRadius: '0.85rem',
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                }}
              >
                <span style={{ color: colors.text, fontSize: '1rem', lineHeight: 1.4, flex: '1 1 auto', minWidth: 0 }}>
                  Your year of home tasks is ready
                </span>
                <button
                  type="button"
                  className="tasks-ready-ok"
                  onClick={() => {
                    dismissTasksReady(typeof localStorage === 'undefined' ? null : localStorage, client && client.id);
                    setTasksReadyHidden(true);
                  }}
                  style={{
                    flex: '0 0 auto',
                    minHeight: 44,
                    minWidth: '2.75rem',
                    padding: '0.4rem 0.8rem',
                    border: 'none',
                    background: 'transparent',
                    color: brandColor,
                    fontWeight: 650,
                    fontSize: '1rem',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    overflowWrap: 'normal',
                    wordBreak: 'keep-all',
                  }}
                >
                  OK
                </button>
              </div>
            ) : null}
            {!readOnlyHome && !homeSetupFinished(client) && homePromptOpen ? (
              <HomeProfileQuestions
                mode="wizard"
                value={homeProfile}
                onChange={setHomeProfile}
                brandColor={brandColor}
                onFinish={() => saveHomeProfile(homeProfile)}
                onSkip={() => {
                  dismissHomeProfilePrompt(window.localStorage, client.id);
                  setHomePromptHidden(true);
                  setHomePromptOpen(false);
                  reassignTasks(
                    client.id,
                    client.agent_id,
                    propertyData.property_type || client.property_type,
                    propertyData.features || client.features || []
                  ).then(() => loadTasks()).catch((error) => {
                    console.error('Baseline tasks were not created yet', error);
                  });
                }}
              />
            ) : null}
            {!readOnlyHome && !homeSetupFinished(client) && !homePromptOpen && !homePromptHidden && homeProfileIsBlank(client) ? (
              <HomeProfilePromptCard
                onStart={() => setHomePromptOpen(true)}
                onDismiss={() => {
                  dismissHomeProfilePrompt(window.localStorage, client.id);
                  setHomePromptHidden(true);
                  reassignTasks(
                    client.id,
                    client.agent_id,
                    propertyData.property_type || client.property_type,
                    propertyData.features || client.features || []
                  ).then(() => loadTasks()).catch((error) => {
                    console.error('Baseline tasks were not created yet', error);
                  });
                }}
              />
            ) : null}
            <HomeHistoryRequestNotice colors={colors} onCreate={() => goToTab('home-history')} />
            <HomePreviousOwnerCard colors={colors} />
            {!readOnlyHome && (
            <YearlyCheckinCard
              colors={colors}
              brandColor={brandColor}
              client={client}
              onAvailability={setCheckinVisible}
              onSaved={loadTasks}
            />
            )}
            {tourChoiceStored && !readOnlyHome ? (
            <HomePhaseCards
              colors={colors}
              brandColor={brandColor}
              state={client.state}
              checkinVisible={checkinVisible}
              onOpenTask={openRegionalTask}
              onChanged={async (result) => {
                const patch = result && result.clientPatch;
                if (patch && patch.features) {
                  setPropertyData((prev) => Object.assign({}, prev, { features: patch.features }));
                  client.features = patch.features;
                }
                if (patch) Object.assign(client, patch);
                await loadTasks();
              }}
            />
            ) : null}
            <OverviewPropertyHero
              url={client.property_photo_url}
              address={client.address}
              propertyType={client.property_type}
              isMobile={isMobile}
            />

            {(() => {
              const homeGroups = calendarTaskGroups(tasks, { now: new Date() });
              const overdueNow = homeGroups.overdue;
              const dueThisWeek = homeGroups.thisWeek;
              return (
                <div style={{marginBottom: '2rem'}}>
                  {overdueNow.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h2 style={{...styles.sectionTitle, color: colors.text, fontSize: isMobile ? '1.3rem' : '1.5rem'}}>
                        Overdue
                      </h2>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                        {overdueNow.map((task) => renderTaskCard(task))}
                      </div>
                    </div>
                  )}
                  <h2 style={{...styles.sectionTitle, color: colors.text, fontSize: isMobile ? '1.3rem' : '1.5rem'}}>
                    Due this week
                  </h2>
                  {dueThisWeek.length === 0 ? (
                    <p style={{color: colors.textSecondary, marginTop: 0}}>
                      {overdueNow.length > 0 ? 'Nothing else is due this week.' : 'Nothing is due this week.'}
                    </p>
                  ) : (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                      {dueThisWeek.map((task) => renderTaskCard(task))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Agent Section */}
            {agent && (
              <div>
                <h2 style={{...styles.sectionTitle, color: colors.text, fontSize: isMobile ? '1.3rem' : '1.5rem'}}>
                  Sponsor
                </h2>

                {/* Brokerage Branding */}
                {(agent.logo_url || agent.brokerage_photo_url) && (
                  <div style={{
                    ...styles.propertyCard,
                    background: colors.surface,
                    border: `1px solid ${colors.border}`,
                    marginBottom: '1.5rem',
                    textAlign: 'center',
                    padding: isMobile ? '1.5rem' : '2rem'
                  }}>
                    {agent.logo_url && (
                      <div style={{marginBottom: agent.brokerage_photo_url ? '1.5rem' : '0'}}>
                        <img
                          src={agent.logo_url}
                          alt="Brokerage Logo"
                          style={{
                            maxWidth: isMobile ? '200px' : '250px',
                            maxHeight: '80px',
                            objectFit: 'contain',
                            margin: '0 auto'
                          }}
                        />
                      </div>
                    )}
                    {agent.brokerage_photo_url && (
                      <div>
                        <img
                          src={agent.brokerage_photo_url}
                          alt="Brokerage"
                          style={{
                            width: '100%',
                            maxHeight: isMobile ? '200px' : '300px',
                            objectFit: 'cover',
                            borderRadius: '0.5rem'
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Agent Information Card */}
                <div style={{
                  ...styles.propertyCard,
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  padding: isMobile ? '1.5rem' : '2rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box'
                }}>
                  {agent.headshot_url && (
                    <img
                      src={agent.headshot_url}
                      alt={agent.first_name}
                      style={{
                        width: isMobile ? '100px' : '140px',
                        height: isMobile ? '100px' : '140px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: `4px solid ${brandColor}`,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        marginBottom: '1.5rem'
                      }}
                    />
                  )}

                  <h3 style={{
                    margin: '0 0 0.5rem 0',
                    fontSize: isMobile ? '1.4rem' : '1.8rem',
                    color: colors.text,
                    fontWeight: '600',
                    ...WRAP_TEXT
                  }}>
                    {[agent.first_name, agent.last_name].filter(Boolean).join(' ') || 'Your sponsor'}
                  </h3>

                  {(agent.team_name || agent.brokerage) && (
                    <div style={{
                      margin: '0 0 1.5rem 0',
                      color: colors.textSecondary,
                      fontSize: '1rem',
                      maxWidth: '100%',
                      ...WRAP_TEXT
                    }}>
                      {agent.team_name && (
                        <p style={{ margin: 0, ...WRAP_TEXT }}>{agent.team_name}</p>
                      )}
                      {agent.brokerage && (
                        <p style={{ margin: agent.team_name ? '0.35rem 0 0' : 0, ...WRAP_TEXT }}>{agent.brokerage}</p>
                      )}
                    </div>
                  )}

                  <div style={{
                    width: '100%',
                    minWidth: 0,
                    textAlign: 'left',
                    borderTop: `1px solid ${colors.border}`,
                    paddingTop: '1.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                  }}>
                    {agent.email && (
                      <a
                        href={`mailto:${agent.email}`}
                        style={{
                          display: 'block',
                          color: brandColor,
                          textDecoration: 'none',
                          fontSize: '1rem',
                          padding: '0.75rem',
                          background: colors.surfaceAlt,
                          borderRadius: isMobile ? '1.25rem' : '0.5rem',
                          transition: 'all 0.2s ease',
                          boxSizing: 'border-box',
                          ...WRAP_TEXT
                        }}
                      >
                        <div style={{fontSize: '0.8125rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem'}}>Email</div>
                        <div style={{fontWeight: '500', ...WRAP_TEXT}}>{agent.email}</div>
                      </a>
                    )}

                    {phoneTelHref(agent.phone) && (
                      <a
                        href={phoneTelHref(agent.phone)}
                        style={{
                          display: 'block',
                          color: brandColor,
                          textDecoration: 'none',
                          fontSize: '1rem',
                          padding: '0.75rem',
                          background: colors.surfaceAlt,
                          borderRadius: isMobile ? '1.25rem' : '0.5rem',
                          transition: 'all 0.2s ease',
                          boxSizing: 'border-box',
                          ...WRAP_TEXT
                        }}
                      >
                        <div style={{fontSize: '0.8125rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem'}}>Phone</div>
                        <div style={{fontWeight: '500', ...WRAP_TEXT}}>{formatPhoneDisplay(agent.phone)}</div>
                      </a>
                    )}

                    {agent.website && (
                      <a
                        href={agent.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'block',
                          color: brandColor,
                          textDecoration: 'none',
                          fontSize: '1rem',
                          padding: '0.75rem',
                          background: colors.surfaceAlt,
                          borderRadius: isMobile ? '1.25rem' : '0.5rem',
                          transition: 'all 0.2s ease',
                          boxSizing: 'border-box',
                          ...WRAP_TEXT
                        }}
                      >
                        <div style={{fontSize: '0.8125rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem'}}>Website</div>
                        <div style={{fontWeight: '500', ...WRAP_TEXT}}>{agent.website}</div>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {(activeTab === 'tasks' || activeTab === 'projects') && listRefreshing ? (
          <p role="status" style={{ color: colors.textSecondary, margin: '0 0 0.75rem' }}>Refreshing…</p>
        ) : null}

        {activeTab === 'tasks' && (() => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const groups = calendarTaskGroups(tasks, { showCompleted: showCompletedTasks, now: today });
          const overdueNow = groups.overdue;
          const thisWeekTasks = groups.thisWeek;

          // Generate calendar for current month
          const year = currentCalendarMonth.getFullYear();
          const month = currentCalendarMonth.getMonth();
          const firstDay = new Date(year, month, 1);
          const lastDay = new Date(year, month + 1, 0);
          const daysInMonth = lastDay.getDate();
          const startingDayOfWeek = firstDay.getDay();

          // Build calendar grid
          const calendarDays = [];
          for (let i = 0; i < startingDayOfWeek; i++) {
            calendarDays.push(null); // Empty cells before month starts
          }
          for (let day = 1; day <= daysInMonth; day++) {
            calendarDays.push(day);
          }

          // Check which days have tasks
          const getTasksForDate = (day) => {
            if (!day) return [];
            return tasksOnDay(groups.dots, year, month, day);
          };

          // Get tasks for selected date
          const selectedDateTasks = selectedCalendarDate
            ? getTasksForDate(selectedCalendarDate)
            : [];

          const yearWindow = visibleTaskList(tasks, {
            showCompleted: showCompletedTasks,
            now: today,
            fullYear: showFullYear,
            withinDays: 60,
          });
          const allTasksSorted = yearWindow.tasks;

          const monthNames = ["January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"];

          return (
            <div data-tour="tasks">
              <MonthChecklist tasks={tasks} colors={colors} brandColor={brandColor} />
              {/* Control Bar */}
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                marginBottom: '1.5rem',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                {/* Left side - View toggles */}
                <div style={{display: 'flex', gap: '0.75rem', flexWrap: 'wrap'}}>
                  {/* View Mode Toggle */}
                  <div style={{
                    display: 'inline-flex',
                    background: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: isMobile ? '1.25rem' : '0.5rem',
                    overflow: 'hidden'
                  }}>
                    <button
                      onClick={() => setTaskViewMode('calendar')}
                      style={{
                        padding: isMobile ? '0.625rem 1.25rem' : '0.5rem 1rem',
                        background: taskViewMode === 'calendar' ? brandColor : 'transparent',
                        color: taskViewMode === 'calendar' ? 'white' : colors.text,
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: isMobile ? '0.95rem' : '0.9rem',
                        fontWeight: taskViewMode === 'calendar' ? '600' : 'normal',
                        minHeight: isMobile ? '44px' : '36px',
                        transition: 'all 0.2s'
                      }}
                    >
                      Calendar
                    </button>
                    <button
                      onClick={() => setTaskViewMode('list')}
                      style={{
                        padding: isMobile ? '0.625rem 1.25rem' : '0.5rem 1rem',
                        background: taskViewMode === 'list' ? brandColor : 'transparent',
                        color: taskViewMode === 'list' ? 'white' : colors.text,
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: isMobile ? '0.95rem' : '0.9rem',
                        fontWeight: taskViewMode === 'list' ? '600' : 'normal',
                        minHeight: isMobile ? '44px' : '36px',
                        transition: 'all 0.2s'
                      }}
                    >
                      List
                    </button>
                  </div>

                </div>

                {/* Right side - Add Task Button */}
                {!readOnlyHome && (
                <button
                  onClick={() => setShowAddTaskModal(true)}
                  style={{
                    padding: isMobile ? '0.625rem 1.25rem' : '0.5rem 1.5rem',
                    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: isMobile ? '1.25rem' : '0.5rem',
                    cursor: 'pointer',
                    fontSize: isMobile ? '0.95rem' : '0.9rem',
                    fontWeight: '600',
                    minHeight: isMobile ? '44px' : '36px',
                    boxShadow: '0 2px 8px rgba(89, 196, 196, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  Add Task
                </button>
                )}
              </div>
              <div style={{ ...styles.propertyCard, background: colors.surface, border: `1px solid ${colors.border}`, padding: 0, marginBottom: '1rem' }}>
                <button
                  type="button"
                  className="app-menu-row"
                  data-testid="show-completed"
                  onClick={() => {
                    recordControlClick('show-completed', process.env);
                    setShowCompletedTasks((current) => !current);
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem 1.25rem',
                    border: 'none',
                    background: 'transparent',
                    color: colors.text,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '1rem',
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    minHeight: '56px',
                    textAlign: 'left',
                    boxSizing: 'border-box',
                  }}
                >
                  <span style={{ flex: 1, textAlign: 'left' }}>
                    {showCompletedTasks ? 'Hide Completed' : 'Show Completed'}
                  </span>
                </button>
              </div>

              {taskViewMode === 'calendar' ? (
                <>
                  {showCompletedTasks ? (
                    <div style={{marginBottom: '2rem'}}>
                      <h2 style={{...styles.sectionTitle, color: colors.text, fontSize: isMobile ? '1.3rem' : '1.5rem'}}>
                        Completed ({groups.completed.length})
                      </h2>
                      {groups.completed.length === 0 ? (
                        <div style={{
                          ...styles.propertyCard,
                          background: colors.surface,
                          border: `1px solid ${colors.border}`,
                          textAlign: 'center',
                          padding: '2rem',
                          color: colors.textSecondary
                        }}>
                          No completed tasks
                        </div>
                      ) : (
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                          {groups.completed.map((t) => renderTaskCard(t))}
                        </div>
                      )}
                    </div>
                  ) : null}
                  {overdueNow.length > 0 && (
                    <div style={{marginBottom: '2rem'}}>
                      <h2 style={{...styles.sectionTitle, color: colors.text, fontSize: isMobile ? '1.3rem' : '1.5rem'}}>
                        Overdue
                      </h2>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                        {overdueNow.map((t) => renderTaskCard(t))}
                      </div>
                    </div>
                  )}

                  <div style={{marginBottom: '2rem'}}>
                    <h2 style={{...styles.sectionTitle, color: colors.text, fontSize: isMobile ? '1.3rem' : '1.5rem'}}>
                      Due This Week
                    </h2>
                    {thisWeekTasks.length === 0 ? (
                      <div style={{
                        ...styles.propertyCard,
                        background: colors.surface,
                        border: `1px solid ${colors.border}`,
                        textAlign: 'center',
                        padding: '2.5rem',
                        color: colors.textSecondary
                      }}>
                        <div style={{fontSize: '3rem', marginBottom: '1rem'}}>✓</div>
                        <div style={{fontSize: '1.1rem', fontWeight: 600, color: colors.text, marginBottom: '0.5rem'}}>
                          All Caught Up!
                        </div>
                        <div style={{fontSize: '0.95rem', lineHeight: '1.5'}}>
                          No tasks due this week. Check back later for upcoming maintenance items.
                        </div>
                      </div>
                    ) : (
                      <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                        {thisWeekTasks.map((t) => renderTaskCard(t))}
                      </div>
                    )}
                  </div>

                  {/* Calendar Section */}
                  <div style={{marginBottom: '2rem'}}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                      <button
                        onClick={() => setCurrentCalendarMonth(new Date(year, month - 1, 1))}
                        style={{
                          ...styles.smallBtn,
                          padding: '0.5rem 1rem',
                          minHeight: '40px',
                          fontSize: '1.2rem'
                        }}
                      >
                        ‹
                      </button>
                      <h3 style={{
                        margin: 0,
                        color: colors.text,
                        fontSize: isMobile ? '1.1rem' : '1.3rem',
                        fontWeight: '600'
                      }}>
                        {monthNames[month]} {year}
                      </h3>
                      <button
                        onClick={() => setCurrentCalendarMonth(new Date(year, month + 1, 1))}
                        style={{
                          ...styles.smallBtn,
                          padding: '0.5rem 1rem',
                          minHeight: '40px',
                          fontSize: '1.2rem'
                        }}
                      >
                        ›
                      </button>
                    </div>

                    {/* Calendar Grid */}
                    <div style={{
                      background: colors.surface,
                      border: `1px solid ${colors.border}`,
                      borderRadius: isMobile ? '1.25rem' : '16px',
                      padding: isMobile ? '0.5rem' : '1.5rem',
                      maxWidth: '100%',
                      overflowX: 'auto',
                      boxSizing: 'border-box',
                      width: '100%'
                    }}>
                      {/* Day headers */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, minmax(2.4rem, 1fr))',
                        gap: isMobile ? '2px' : '0.5rem',
                        marginBottom: isMobile ? '0.25rem' : '0.5rem',
                        textAlign: 'center',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}>
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                          <div key={i} style={{
                            fontSize: isMobile ? '0.7rem' : '0.85rem',
                            fontWeight: '600',
                            color: colors.textSecondary,
                            padding: isMobile ? '0.2rem 0' : '0.5rem 0',
                            boxSizing: 'border-box',
                            overflow: 'hidden'
                          }}>
                            {day}
                          </div>
                        ))}
                      </div>

                      {/* Calendar days */}
                      <div className="calendar-days" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, minmax(2.4rem, 1fr))',
                        gap: isMobile ? '2px' : '0.5rem',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}>
                        {calendarDays.map((day, index) => {
                          const dayTasks = day ? getTasksForDate(day) : [];
                          const dayMark = calendarDayIndicator(dayTasks.length);
                          const hasTasks = Boolean(dayMark);
                          const isSelected = day === selectedCalendarDate;
                          const isToday = day && new Date(year, month, day).toDateString() === today.toDateString();

                          return (
                            <button
                              key={index}
                              className={day ? 'calendar-day' : undefined}
                              aria-label={day ? `${monthNames[month]} ${day}, ${dayTasks.length} ${dayTasks.length === 1 ? 'task' : 'tasks'}` : undefined}
                              onClick={() => day && setSelectedCalendarDate(isSelected ? null : day)}
                              disabled={!day}
                              style={{
                                aspectRatio: '1',
                                minHeight: isMobile ? '36px' : '50px',
                                border: isToday && !isSelected ? `${isMobile ? '1px' : '2px'} solid ${brandColor}` : `${isMobile ? '1px' : '2px'} solid transparent`,
                                borderRadius: isMobile ? '1rem' : '0.5rem',
                                background: !day ? 'transparent' :
                                  isSelected ? brandColor :
                                  isToday ? colors.surfaceAlt :
                                  colors.surface,
                                color: !day ? 'transparent' :
                                  isSelected ? 'white' :
                                  colors.text,
                                cursor: day ? 'pointer' : 'default',
                                fontSize: isMobile ? '0.75rem' : '1rem',
                                fontWeight: isToday ? '700' : '500',
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                WebkitTapHighlightColor: 'transparent',
                                boxSizing: 'border-box',
                                padding: 0,
                                margin: 0,
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: day ? '2.4rem' : 0,
                                overflow: 'hidden',
                                textOverflow: 'clip',
                                whiteSpace: 'nowrap',
                                fontVariantNumeric: 'tabular-nums',
                                overflowWrap: 'normal',
                                wordBreak: 'keep-all',
                                overflow: 'visible'
                              }}
                              onMouseEnter={(e) => day && !isSelected && (e.currentTarget.style.background = colors.surfaceAlt)}
                              onMouseLeave={(e) => day && !isSelected && !isToday && (e.currentTarget.style.background = colors.surface)}
                            >
                              {day}
                              {dayMark && (
                                <span
                                  data-testid="calendar-day-mark"
                                  style={{
                                    position: 'absolute',
                                    bottom: '3px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '2px',
                                    lineHeight: 1,
                                  }}
                                >
                                  <span style={{
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '50%',
                                    background: isSelected ? 'white' : brandColor,
                                    flex: '0 0 auto',
                                  }} />
                                  {dayMark.count ? (
                                    <span style={{
                                      fontSize: isMobile ? '0.6rem' : '0.65rem',
                                      fontWeight: 700,
                                      color: isSelected ? 'white' : brandColor,
                                    }}>{dayMark.count}</span>
                                  ) : null}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Selected Date Tasks */}
                  {selectedCalendarDate && (
                    <div style={{marginTop: '2rem'}}>
                      <h2 style={{...styles.sectionTitle, color: colors.text, fontSize: isMobile ? '1.1rem' : '1.3rem'}}>
                        {monthNames[month]} {selectedCalendarDate}, {year}
                      </h2>
                      {selectedDateTasks.length === 0 ? (
                        <div style={{
                          ...styles.propertyCard,
                          background: colors.surface,
                          border: `1px solid ${colors.border}`,
                          textAlign: 'center',
                          padding: '2rem',
                          color: colors.textSecondary
                        }}>
                          <div style={{fontSize: '1rem', color: colors.text}}>
                            No tasks scheduled for this day
                          </div>
                        </div>
                      ) : (
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                          {selectedDateTasks.map((t) => renderTaskCard(t))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* List View */
                <div>
                  <h2 style={{...styles.sectionTitle, color: colors.text, fontSize: isMobile ? '1.3rem' : '1.5rem', marginBottom: '1rem'}}>
                    {showFullYear ? `All tasks (${allTasksSorted.length})` : `Coming up (${allTasksSorted.length})`}
                  </h2>
                  {!showFullYear && (
                    <p style={{ color: colors.textSecondary, marginTop: 0 }}>
                      Overdue tasks and the next 60 days. The rest of the year stays on the calendar.
                    </p>
                  )}
                  {allTasksSorted.length === 0 ? (
                    <div style={{
                      ...styles.propertyCard,
                      background: colors.surface,
                      border: `1px solid ${colors.border}`,
                      textAlign: 'center',
                      padding: '2rem',
                      color: colors.textSecondary
                    }}>
                      No tasks found
                    </div>
                  ) : (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                      {allTasksSorted.map((t) => renderTaskCard(t))}
                    </div>
                  )}
                  {yearWindow.hidden > 0 && !showFullYear && (
                    <button
                      type="button"
                      onClick={() => setShowFullYear(true)}
                      style={{ ...styles.smallBtn, minHeight: '44px', marginTop: '1rem' }}
                    >
                      See full year ({yearWindow.hidden} more)
                    </button>
                  )}
                  {showFullYear && (
                    <button
                      type="button"
                      onClick={() => setShowFullYear(false)}
                      style={{ ...styles.smallBtn, minHeight: '44px', marginTop: '1rem' }}
                    >
                      Show the next 60 days
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'property' && (
          <div>
            <div style={styles.section}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                <h2 data-tour="property" style={{...styles.sectionTitle, color: colors.text}}>Property Details</h2>
                {!readOnlyHome && (
                <button
                  style={{...styles.button, width: 'auto'}}
                  onClick={() => editingProperty ? setEditingProperty(false) : setEditingProperty(true)}
                >
                  {editingProperty ? 'Cancel' : 'Edit'}
                </button>
                )}
              </div>

              <div id="home-detail-address" style={{...styles.propertyCard, background: colors.surface, border: `1px solid ${colors.border}`}}>
                <PropertyPhoto
                  url={client.property_photo_url}
                  editing={editingProperty}
                  uploading={uploadingPropertyPhoto}
                  onFile={handlePropertyPhotoChange}
                  colors={colors}
                  brandColor={brandColor}
                  isMobile={isMobile}
                />
                {editingProperty ? (
                  <div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Street Address</label>
                      <input
                        type="text"
                        value={propertyData.address}
                        onChange={(e) => setPropertyData({...propertyData, address: e.target.value})}
                        style={{...styles.input, background: colors.surface, border: `2px solid ${brandColor}`, color: colors.text}}
                      />
                    </div>
                    <div style={{display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '1rem'}}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>City</label>
                        <input
                          type="text"
                          value={propertyData.city}
                          onChange={(e) => setPropertyData({...propertyData, city: e.target.value})}
                          style={styles.input}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>State</label>
                        <input
                          type="text"
                          value={propertyData.state}
                          onChange={(e) => setPropertyData({...propertyData, state: e.target.value})}
                          style={styles.input}
                          maxLength={2}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>ZIP</label>
                        <input
                          type="text"
                          value={propertyData.zip_code}
                          onChange={(e) => setPropertyData({...propertyData, zip_code: e.target.value})}
                          style={styles.input}
                          maxLength={5}
                        />
                      </div>
                    </div>
                    <CountyField
                      zip={propertyData.zip_code}
                      state={propertyData.state}
                      fips={propertyData.property_county_fips}
                      source={propertyData.property_county_source}
                      colors={colors}
                      onChange={(county) => setPropertyData((current) => ({ ...current, ...county }))}
                    />
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Property Type</label>
                      <select
                        value={propertyData.property_type}
                        onChange={(e) => setPropertyData({...propertyData, property_type: e.target.value})}
                        style={styles.input}
                      >
                        {propertyTypes.map(type => (
                          <option key={type.id} value={type.id}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      style={styles.button}
                      onClick={handleSaveProperty}
                    >
                      Save Changes
                    </button>
                  </div>
                ) : (
                  <div>
                    <p style={{color: colors.text}}><strong>Address:</strong> {client.address || 'Not set'}</p>
                    <p style={{color: colors.text}}><strong>City:</strong> {client.city || 'Not set'}, <strong>State:</strong> {client.state || 'FL'} <strong>ZIP:</strong> {client.zip_code || 'Not set'}</p>
                    <p style={{color: colors.text}}><strong>County:</strong> {countyLabel(client.property_county_fips) || 'Not set'}</p>
                    <p style={{color: colors.text}}><strong>Property Type:</strong> {propertyTypes.find(pt => pt.id === client.property_type)?.label || 'Single Family Home'}</p>
                  </div>
                )}
              </div>
            </div>

            <div id="home-detail-plan" style={styles.section}>
              <h2 style={{...styles.sectionTitle, color: colors.text}}>Maintenance plan</h2>
              <HomeownerPlanStep
                hideRoofAge
                hideFilter
                hideAcAge
                initial={{
                  insurance_renewal_date: client.insurance_renewal_date,
                  insurance_term_months: client.insurance_term_months,
                  filter_type: client.filter_type,
                  flood_renewal_date: client.flood_renewal_date,
                  flood_term_months: client.flood_term_months,
                  warranty_renewal_date: client.warranty_renewal_date,
                  warranty_term_months: client.warranty_term_months,
                }}
                saving={savingPlan}
                brandColor={brandColor}
                showSkip={false}
                showPolicies
                pinSave={isMobile}
                saveLabel="Save answers"
                readOnly={readOnlyHome}
                onSave={saveMaintenancePlan}
              />
            </div>

            <div id="home-detail-facts" style={styles.section}>
              <h2 data-tour="home-facts" style={{ ...styles.sectionTitle, color: colors.text }}>Home facts</h2>
              <p style={{ color: colors.textSecondary, marginTop: 0 }}>
                Walls, roof, air conditioners, water heaters, and appliances. The same answers as setup.
              </p>
              <HomeProfileQuestions
                mode="editor"
                value={homeProfile}
                onChange={setHomeProfile}
                brandColor={brandColor}
                readOnly={readOnlyHome}
              />
              <HomeFactsFields
                value={{
                  ...systemData,
                  features: propertyData.features,
                }}
                onChange={(next) => {
                  setSystemData((prev) => ({ ...prev, ...next }));
                  if (next.features) setPropertyData((prev) => ({ ...prev, features: next.features }));
                }}
                brandColor={brandColor}
                readOnly={readOnlyHome}
                equipment={(
            <div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', marginTop: '0.5rem'}}>
                <h3 style={{ margin: 0, color: colors.text, fontSize: '1.05rem' }}>Appliances</h3>
                {!readOnlyHome && (
                <button
                  style={{...styles.button, width: 'auto', padding: '0.65rem 1.25rem'}}
                  onClick={() => openInventoryEditor(null)}
                >
                  Add appliance
                </button>
                )}
              </div>
              <p style={{ color: colors.textSecondary, marginTop: 0 }}>
                Fridge, washer, and the other appliances. Air conditioners and water heaters are the cards above.
              </p>
            </div>
                )}
              />
              {!readOnlyHome && (
                <>
                  <button
                    type="button"
                    style={{ ...styles.button, marginTop: '0.75rem' }}
                    onClick={saveHomeFacts}
                  >
                    Save home facts
                  </button>
                  {factsNote ? (
                    <p role="status" style={{ color: colors.text, margin: '0.5rem 0 0', fontWeight: 650 }}>{factsNote}</p>
                  ) : null}
                  <button
                    type="button"
                    aria-busy={rebuildingTasks}
                    disabled={rebuildingTasks}
                    style={{ ...styles.button, marginTop: '0.5rem', background: 'transparent', color: colors.text, border: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                    onClick={rebuildHomeTasks}
                  >
                    {rebuildingTasks ? 'Rebuilding...' : 'Rebuild tasks'}
                  </button>
                  {rebuildNote ? (
                    <p role="status" style={{ color: colors.text, margin: '0.5rem 0 0', fontWeight: 650 }}>{rebuildNote}</p>
                  ) : null}
                  <p style={{ color: colors.textSecondary, margin: '0.4rem 0 0', fontSize: '0.9rem' }}>
                    Rebuild uses the last saved facts. Completed tasks stay. Open tasks that no longer fit this home are removed, and missing ones come back.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div style={{minWidth: 0, maxWidth: '100%'}}>
            <div style={{...styles.section, minWidth: 0, maxWidth: '100%'}}>
              <h2 data-tour="contacts" style={{...styles.sectionTitle, color: colors.text, marginBottom: '0.35rem'}}>Utility and Service Providers</h2>
              <p style={{color: colors.textSecondary, marginTop: 0, marginBottom: '1rem'}}>
                Utilities and other service contacts for this home.
              </p>
              <ErrorBoundary>
                <PropertyContacts
                  inviteCodeFromProps={client?.invite_code}
                  colors={colors}
                  brandColor={brandColor}
                  readOnly={readOnlyHome}
                />
              </ErrorBoundary>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            {showMove && !readOnlyHome ? (
              <MoveHome
                colors={colors}
                brandColor={brandColor}
                sponsored={!!client.agent_id}
                onCancel={() => setShowMove(false)}
                onMoved={(result) => {
                  if (onClientReplaced && result && result.client) onClientReplaced(result.client);
                }}
              />
            ) : morePane ? (
            <div>
              <button
                type="button"
                aria-label="Back"
                onClick={() => setMorePane(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: colors.primary || '#59c4c4',
                  fontSize: '1rem',
                  fontWeight: 650,
                  padding: '0 0 1rem',
                  cursor: 'pointer',
                  minHeight: '44px',
                }}
              >
                ‹ More
              </button>
              {morePane === 'notifications' ? (
                notifPrefs ? (
                  <HomeownerNotificationSettings
                    audience={notifAudience}
                    preferences={notifPrefs}
                    onPatch={handleUpdateNotifPrefs}
                  />
                ) : (
                  <p style={{ color: colors.textSecondary }}>Loading notification settings...</p>
                )
              ) : morePane === 'invite' ? (
                <InviteFriendPane colors={colors} invitePreview={invitePreview} />
              ) : morePane === 'subscription' ? (
                <HomeownerPaywall page plan={homePlan} />
              ) : (
                <div>
                  <h2 style={homeownerSectionHeading}>Sponsor</h2>
                  {agent ? (
                    <div style={{...styles.propertyCard, background: colors.surface, border: `1px solid ${colors.border}`, padding: '1rem 1.25rem', marginBottom: '1rem'}}>
                      <p style={{ margin: '0 0 0.35rem', color: colors.text, fontWeight: 650, fontSize: '1.05rem' }}>
                        {[agent.first_name, agent.last_name].filter(Boolean).join(' ') || 'Your sponsor'}
                      </p>
                      {(agent.team_name || agent.brokerage) ? (
                        <p style={{ margin: '0 0 0.75rem', color: colors.textSecondary }}>
                          {[agent.team_name, agent.brokerage].filter(Boolean).join(' · ')}
                        </p>
                      ) : null}
                      {agent.email ? (
                        <p style={{ margin: '0.35rem 0' }}>
                          <a href={`mailto:${agent.email}`} style={{ color: brandColor, textDecoration: 'none' }}>{agent.email}</a>
                        </p>
                      ) : null}
                      {phoneTelHref(agent.phone) ? (
                        <p style={{ margin: '0.35rem 0' }}>
                          <a href={phoneTelHref(agent.phone)} style={{ color: brandColor, textDecoration: 'none' }}>{formatPhoneDisplay(agent.phone)}</a>
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <p style={{ color: colors.textSecondary, marginTop: 0 }}>
                      This home doesn't have a sponsor yet.
                    </p>
                  )}
                  {!readOnlyHome && (
                    <SponsorCodeForm
                      colors={colors}
                      brandColor={brandColor}
                      onAccepted={(result) => {
                        if (onClientReplaced && result && result.client) onClientReplaced(result.client);
                        showToast('Sponsor updated.', 'success');
                      }}
                    />
                  )}
                </div>
              )}
            </div>
            ) : (
            <div>
            <div style={{marginBottom: '1.5rem'}}>
                <h2 style={{...styles.sectionTitle, color: colors.text, marginTop: 0, marginBottom: '0.75rem'}}>More</h2>
                <div style={{...styles.propertyCard, background: colors.surface, border: `1px solid ${colors.border}`, padding: '0'}}>
                  {moreLinks.map((item) => (
                    <button
                      key={item.id}
                      className="app-menu-row"
                      data-tour={item.id}
                      onClick={() => goToTab(item.id)}
                      style={{
                        width: '100%',
                        padding: '1rem 1.25rem',
                        border: 'none',
                        borderBottom: `1px solid ${colors.border}`,
                        background: 'transparent',
                        color: colors.text,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        fontSize: '1rem',
                        fontWeight: '500',
                        fontFamily: 'inherit',
                        minHeight: '56px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <span style={{flex: 1, textAlign: 'left'}}>
                        <span style={{ display: 'block' }}>{item.label}</span>
                        {item.id === 'home-history' && homePlan && homePlan.entitled === false ? (
                          <span style={{ display: 'block', color: colors.textSecondary, fontWeight: 450, fontSize: '0.85rem' }}>
                            {HOME_REPORT_PLAN_MESSAGE}
                          </span>
                        ) : null}
                      </span>
                      <span style={{color: colors.textSecondary}}>›</span>
                    </button>
                  ))}
                  {!isNativeApp() && canManagePlan && (
                    <button
                      type="button"
                      className="app-menu-row"
                      onClick={openManagePlan}
                      style={{
                        width: '100%',
                        padding: '1rem 1.25rem',
                        border: 'none',
                        borderBottom: `1px solid ${colors.border}`,
                        background: 'transparent',
                        color: colors.text,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        fontSize: '1rem',
                        fontWeight: '500',
                        fontFamily: 'inherit',
                        minHeight: '56px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <span style={{ flex: 1, textAlign: 'left' }}>Manage plan</span>
                      <span style={{ color: colors.textSecondary }}>›</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="app-menu-row"
                    data-testid="homeowner-subscription-row"
                    onClick={() => setMorePane('subscription')}
                    style={{
                      width: '100%',
                      padding: '1rem 1.25rem',
                      border: 'none',
                      borderBottom: `1px solid ${colors.border}`,
                      background: 'transparent',
                      color: colors.text,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      fontSize: '1rem',
                      fontWeight: '500',
                      fontFamily: 'inherit',
                      minHeight: '56px',
                      boxSizing: 'border-box',
                    }}
                  >
                    <span style={{ flex: 1, textAlign: 'left' }}>Subscription</span>
                    <span style={{ color: colors.textSecondary }}>›</span>
                  </button>
                </div>
            </div>

            {accountHomes.length > 1 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ ...styles.sectionTitle, color: colors.text, marginTop: 0, marginBottom: '0.75rem' }}>Your homes</h2>
                <div style={{ ...styles.propertyCard, background: colors.surface, border: `1px solid ${colors.border}`, padding: 0 }}>
                  {accountHomes.slice().sort((left, right) => {
                    const rank = (home) => (home.current ? 0 : (home.readOnly || home.archived ? 2 : 1));
                    return rank(left) - rank(right);
                  }).map((home) => {
                    const current = !!(home.current || home.id === client.id);
                    const place = [home.city, home.state].filter(Boolean).join(', ');
                    const label = current ? 'Current' : ((home.readOnly || home.archived) ? 'Archived' : 'Home');
                    return (
                      <button
                        key={home.id}
                        type="button"
                        className="app-menu-row"
                        disabled={current || switchingHome}
                        onClick={() => { if (!current) openListedHome(home); }}
                        style={{
                          width: '100%',
                          padding: '1rem 1.25rem',
                          border: 'none',
                          borderBottom: `1px solid ${colors.border}`,
                          background: 'transparent',
                          color: colors.text,
                          cursor: current ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          fontSize: '1rem',
                          fontWeight: 500,
                          fontFamily: 'inherit',
                          minHeight: '56px',
                          boxSizing: 'border-box',
                          textAlign: 'left',
                          opacity: switchingHome && !current ? 0.7 : 1,
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block' }}>{home.address || 'Home'}</span>
                          {place ? (
                            <span style={{ display: 'block', color: colors.textSecondary, fontSize: '0.9rem', fontWeight: 400 }}>{place}</span>
                          ) : null}
                        </span>
                        <span style={{ color: colors.textSecondary, flexShrink: 0 }}>{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {!readOnlyHome && (
              <button
                type="button"
                onClick={() => setShowMove(true)}
                className="app-menu-row"
                style={{
                  width: '100%',
                  margin: '0 0 1.5rem',
                  padding: '1rem 1.25rem',
                  borderRadius: isMobile ? '1.25rem' : '0.75rem',
                  border: `1px solid ${colors.border}`,
                  background: colors.surface,
                  color: colors.text,
                  textAlign: 'left',
                  fontSize: '1rem',
                  fontWeight: 650,
                  fontFamily: 'inherit',
                  minHeight: '56px',
                  cursor: 'pointer',
                }}
              >
                Move to a new home
              </button>
            )}
            <h2 style={homeownerSectionHeading}>Account</h2>
            <div style={{...styles.propertyCard, background: colors.surface, border: `1px solid ${colors.border}`, marginBottom: '1.5rem', padding: '1rem 1.25rem'}}>
              <AccountSignIn
                mode="homeowner"
                email={client.email}
                colors={colors}
              />
            </div>

            {/* Replay tour */}
            <div style={{marginTop: '2rem'}}>
              <button
                type="button"
                data-tour="settings"
                onClick={startHomeownerTour}
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  border: `2px solid ${brandColor}`,
                  background: colors.surface,
                  color: brandColor,
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  borderRadius: isMobile ? '1.25rem' : '0.75rem',
                  minHeight: isMobile ? '56px' : '48px'
                }}
              >
                Replay tour
              </button>
            </div>

            <HomeownerAccount
              onDeleted={() => onLogout('deleted')}
              onLoggedOut={() => onLogout()}
            />

            <div id="appearance-settings" style={{marginTop: '2rem'}}>
              <h2 style={homeownerSectionHeading}>Appearance</h2>
              <AppearanceChoice />
            </div>

            <div data-tour="more-rows" style={{...styles.propertyCard, background: colors.surface, border: `1px solid ${colors.border}`, padding: 0, marginTop: '1.5rem'}}>
              <button
                type="button"
                className="app-menu-row"
                onClick={() => setMorePane('notifications')}
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  border: 'none',
                  borderBottom: `1px solid ${colors.border}`,
                  background: 'transparent',
                  color: colors.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '1rem',
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  minHeight: '56px',
                  textAlign: 'left',
                  boxSizing: 'border-box',
                }}
              >
                <span>Notification Settings</span>
                <span style={{color: colors.textSecondary}}>›</span>
              </button>
              <button
                type="button"
                className="app-menu-row"
                onClick={() => setMorePane('sponsor')}
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  border: 'none',
                  borderBottom: `1px solid ${colors.border}`,
                  background: 'transparent',
                  color: colors.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '1rem',
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  minHeight: '56px',
                  textAlign: 'left',
                  boxSizing: 'border-box',
                }}
              >
                <span>Sponsor code</span>
                <span style={{color: colors.textSecondary}}>›</span>
              </button>
              <button
                type="button"
                className="app-menu-row"
                data-testid="invite-friend-row"
                onClick={() => setMorePane('invite')}
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  border: 'none',
                  borderBottom: `1px solid ${colors.border}`,
                  background: 'transparent',
                  color: colors.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '1rem',
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  minHeight: '56px',
                  textAlign: 'left',
                  boxSizing: 'border-box',
                }}
              >
                <span>Invite a friend</span>
                <span style={{color: colors.textSecondary}}>›</span>
              </button>
              <a
                className="app-menu-row"
                data-tour="legal"
                href={LEGAL_INDEX_PATH}
                onClick={followPolicyLink}
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  border: 'none',
                  background: 'transparent',
                  color: colors.text,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '1rem',
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  minHeight: '56px',
                  textDecoration: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <span>Legal</span>
                <span style={{color: colors.textSecondary}}>›</span>
              </a>
            </div>

            <div style={{...styles.propertyCard, background: colors.surface, border: `1px solid ${colors.border}`, padding: 0, marginTop: '1.5rem'}}>
              <button
                type="button"
                className="app-menu-row"
                onClick={onLogout}
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  border: 'none',
                  background: 'transparent',
                  color: '#dc2626',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '1rem',
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  minHeight: '56px',
                  textAlign: 'left',
                  boxSizing: 'border-box',
                }}
              >
                <span style={{flex: 1, textAlign: 'left'}}>Logout</span>
              </button>
            </div>
            </div>
            )}
          </div>
        )}

        {activeTab === 'assistant' && (
          <AssistantThread
            messages={chatMessages}
            loading={threadLoading}
            loadingOlder={threadLoadingOlder}
            hasOlder={threadHasOlder}
            onLoadOlder={loadOlderChat}
            onSearch={searchChat}
            onReveal={revealChat}
            onStartFresh={readOnlyHome ? undefined : handleStartFresh}
            onRetry={(msg) => handleSendMessage(msg)}
            onExample={(example) => setUserInput(example)}
            aiLoading={aiLoading}
            colors={colors}
            isMobile={isMobile}
            brandColor={brandColor}
            renderExtra={(msg) => (
              <>
                {msg.role === 'assistant' && msg.recommendedVendors && msg.recommendedVendors.length > 0 && (
                  <div style={{marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${colors.border}`}}>
                    {msg.recommendedVendors.map((vendor, vIdx) => (
                      <div key={vIdx} style={{
                        padding: '0.5rem',
                        background: colors.surfaceAlt,
                        borderRadius: '0.5rem',
                        marginBottom: '0.5rem',
                        fontSize: '0.85rem'
                      }}>
                        <div style={{fontWeight: 600, color: colors.text}}>{vendor.name}</div>
                        {vendor.category ? (
                          <div style={{ marginTop: '0.2rem', color: colors.textSecondary }}>{Array.isArray(vendor.category) ? vendor.category.join(', ') : vendor.category}</div>
                        ) : null}
                        <a href={`tel:${vendor.phone}`} onClick={(event) => gateVendorContact(event, vendor, 'phone', false)} style={{color: brandColor, textDecoration: 'none'}}>
                          {formatPhoneDisplay(vendor.phone) || vendor.phone}
                        </a>
                        <HomeownerDiscount vendor={vendor} color="#10b981" />
                        <p style={{ margin: '0.35rem 0 0', color: colors.textSecondary, fontSize: '0.8rem' }}>
                          {vendor.suggestionLine || providerSuggestionLine(vendor.listSource)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                {msg.role === 'assistant' && msg.suggestTask && msg.taskSuggestion && !readOnlyHome && (
                  <div style={{marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${colors.border}`}}>
                    <button
                      type="button"
                      onClick={() => {
                        setNewTaskData({
                          title: msg.taskSuggestion.title,
                          description: '',
                          due_date: ''
                        });
                        setShowAddTaskModal(true);
                        goToTab('tasks');
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: brandColor,
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        minHeight: '44px'
                      }}
                    >
                      + Create Task for This
                    </button>
                  </div>
                )}
              </>
            )}
          >
            {/* Input Area - Now on BOTTOM like texting */}
            <div>
              {/* Image Upload Preview - Show above input if image selected */}
              {selectedImage && (
                <div style={{
                  background: colors.surfaceAlt,
                  border: `2px solid ${brandColor}`,
                  borderRadius: isMobile ? '1.25rem' : '0.5rem',
                  padding: '0.75rem',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: imagePreviewUrl ? '0.5rem' : '0'
                  }}>
                    <span style={{fontWeight: 500, color: colors.text, fontSize: '0.9rem'}}>{selectedImage.name}</span>
                    <button
                      type="button"
                      onClick={() => handleImageSelect(null)}
                      style={{background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: colors.text, padding: '0 0.5rem'}}
                      title="Remove image"
                      aria-label="Remove photo"
                    >
                      ×
                    </button>
                  </div>
                  {imagePreviewUrl && (
                    <img
                      src={imagePreviewUrl}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: '200px',
                        borderRadius: isMobile ? '1.25rem' : '0.5rem',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  )}
                </div>
              )}

              {assistantError && (
                <div
                  role="alert"
                  style={{
                    marginBottom: '0.75rem',
                    padding: '0.75rem 1rem',
                    borderRadius: '0.5rem',
                    background: isDark ? '#3f1d1d' : '#fef2f2',
                    border: `1px solid ${isDark ? '#7f1d1d' : '#fecaca'}`,
                    color: isDark ? '#fecaca' : '#991b1b',
                    fontSize: '0.95rem',
                    lineHeight: 1.4
                  }}
                >
                  {assistantError}
                </div>
              )}

              {/* Textarea */}
              <textarea
                ref={assistantInputRef}
                data-assistant-input="true"
                value={userInput}
                enterKeyHint="send"
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' || e.shiftKey || (e.nativeEvent && e.nativeEvent.isComposing)) return;
                  e.preventDefault();
                  handleSendMessage();
                }}
                style={{
                  width: '100%',
                  padding: isMobile ? '0.75rem 1rem' : '0.75rem',
                  border: `2px solid ${brandColor}`,
                  borderRadius: isMobile ? '1.25rem' : '0.5rem',
                  fontSize: isMobile ? '1rem' : '1rem',
                  resize: 'none',
                  minHeight: isMobile ? '44px' : '60px',
                  maxHeight: isMobile ? '88px' : '120px',
                  background: isDark ? colors.surfaceAlt : '#FFFFFF',
                  color: colors.text,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  boxShadow: `0 0 0 4px ${brandColor}20`,
                  outline: 'none',
                  marginBottom: '0.75rem',
                  boxSizing: 'border-box'
                }}
                disabled={aiLoading || readOnlyHome}
                placeholder={readOnlyHome ? 'This home is archived.' : 'Type your message...'}
              />

              {/* Buttons */}
              {!readOnlyHome && (
              <div style={{display: 'flex', gap: '0.5rem'}}>
                <label style={{
                  background: colors.surfaceAlt,
                  padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 1.25rem',
                  borderRadius: isMobile ? '1.25rem' : '0.5rem',
                  cursor: 'pointer',
                  border: `2px solid ${colors.borderLight}`,
                  color: colors.text,
                  fontSize: isMobile ? '1rem' : '0.9rem',
                  fontWeight: isMobile ? '600' : 'normal',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: isMobile ? '44px' : '44px'
                }}>
                  Attach
                  <input
                    ref={assistantImageInputRef}
                    type="file"
                    accept="image/*"
                    aria-label="Attach a photo"
                    onChange={(e) => handleImageSelect(e.target.files && e.target.files[0])}
                    style={{display: 'none'}}
                    disabled={aiLoading}
                  />
                </label>
                <button
                  type="button"
                  onClick={handleSendMessage}
                  style={{
                    padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: isMobile ? '1.25rem' : '0.5rem',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: isMobile ? '1rem' : '1rem',
                    minHeight: isMobile ? '44px' : '44px',
                    minWidth: isMobile ? '80px' : 'auto',
                    boxShadow: '0 2px 8px rgba(89, 196, 196, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                  disabled={aiLoading || (!userInput.trim() && !selectedImage)}
                >
                  Send
                </button>
              </div>
              )}
              <HomeownerAiNotice color={colors.textSecondary} sponsorless={sponsorless} />

              <QuestionsLeftNote
                used={countRowsSince(conversations, monthStartIso())}
                limit={monthlyQuestionLimit(process.env)}
                color={colors.textSecondary}
              />
            </div>
          </AssistantThread>
        )}


        {activeTab === 'documents' && (
          <HomeownerDocumentsPanel
            colors={colors}
            isDark={isDark}
            brandColor={brandColor}
            isMobile={isMobile}
            readOnly={readOnlyHome}
            documents={documents}
            folders={documentFolders}
            openFolderId={openDocumentFolderId}
            onOpenFolder={setOpenDocumentFolderId}
            categories={documentCategories}
            title={documentTitle}
            category={documentCategory}
            file={selectedFile}
            uploading={uploadingDocument}
            styles={styles}
            onTitleChange={setDocumentTitle}
            onCategoryChange={setDocumentCategory}
            onFileChange={(nextFile) => {
              setSelectedFile(nextFile);
              if (nextFile) setDocumentTitle((current) => documentTitleAfterFilePick(current, nextFile.name));
            }}
            onUpload={handleUploadDocument}
            onCancelDraft={clearDocumentDraft}
            onView={handleViewDocument}
            onDownload={handleDownloadDocument}
            onDelete={handleDeleteDocument}
            onCreateFolder={handleCreateFolder}
            onRenameFolder={handleRenameFolder}
            onDeleteFolder={handleDeleteFolder}
            onShareFolder={handleShareFolder}
            onShareDocument={handleShareDocument}
            onMoveDocument={handleMoveDocument}
          />
        )}

        {activeTab === 'projects' && (
          <div>
            <h2 data-tour="projects" style={{...styles.sectionTitle, color: colors.text}}>My Projects</h2>
            <p style={{color: colors.textSecondary, marginBottom: '1.5rem'}}>
              {projectChecklistCopy(Boolean(agent), {
                ownProject: projects.some((project) => project && project.created_by_role === 'homeowner'),
              }).intro}
            </p>
            {homePlan && homePlan.entitled === false && (
              <p style={{ color: colors.text, marginTop: '-0.5rem', marginBottom: '1rem' }}>{HOMEOWNER_PLAN_INACTIVE_MESSAGE}</p>
            )}
            {canWriteProjects && !showProjectForm && (
              <button
                type="button"
                onClick={() => setShowProjectForm(true)}
                style={{
                  marginBottom: '1.25rem',
                  minHeight: '44px',
                  padding: '0.7rem 1rem',
                  border: 'none',
                  borderRadius: '0.75rem',
                  background: brandColor,
                  color: '#fff',
                  fontWeight: 650,
                  cursor: 'pointer',
                }}
              >
                New project
              </button>
            )}
            {canWriteProjects && showProjectForm && (
              <HomeownerProjectForm
                colors={colors}
                brandColor={brandColor}
                vendorOptions={projectVendorOptions()}
                busy={savingProject}
                onCancel={() => setShowProjectForm(false)}
                onSubmit={createHomeownerProject}
              />
            )}

            {projects.length === 0 ? (
              <div style={{
                ...styles.emptyState,
                background: colors.surface,
                color: colors.textSecondary,
                textAlign: 'center',
                padding: '3rem 2rem'
              }}>
                <div style={{fontSize: '1.2rem', fontWeight: 600, color: colors.text, marginBottom: '0.75rem'}}>
                  No Projects Yet
                </div>
                <div style={{fontSize: '1rem', lineHeight: '1.6', maxWidth: '400px', margin: '0 auto'}}>
                  {projectChecklistCopy(Boolean(agent)).empty}
                </div>
              </div>
            ) : (
              <>
                {/* Filter Tabs */}
                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  marginBottom: '2rem',
                  flexWrap: 'wrap'
                }}>
                  {['active', 'completed', 'all'].map(filter => {
                    const count = filter === 'all'
                      ? projects.length
                      : projects.filter(p => {
                          const isCompleted = (p.tasks?.filter(t => t.completed).length || 0) === (p.tasks?.length || 0) && p.tasks?.length > 0;
                          return filter === 'completed' ? isCompleted : !isCompleted;
                        }).length;

                    return (
                      <button
                        key={filter}
                        onClick={() => setProjectFilter(filter)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          borderRadius: '2rem',
                          border: `2px solid ${projectFilter === filter ? brandColor : colors.borderLight}`,
                          background: projectFilter === filter ? brandColor : colors.surface,
                          color: projectFilter === filter ? 'white' : colors.text,
                          fontWeight: projectFilter === filter ? '600' : '500',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          transition: 'all 0.2s ease',
                          textTransform: 'capitalize'
                        }}
                      >
                        {filter} ({count})
                      </button>
                    );
                  })}
                </div>

                {(() => {
                  const visibleProjects = projects.filter((project) => {
                    const isCompleted = (project.tasks?.filter(t => t.completed).length || 0) === (project.tasks?.length || 0) && project.tasks?.length > 0;
                    if (projectFilter === 'active') return !isCompleted;
                    if (projectFilter === 'completed') return isCompleted;
                    return true;
                  });
                  if (!visibleProjects.length) {
                    const emptyLine = projectFilter === 'completed'
                      ? 'No completed projects.'
                      : projectFilter === 'all'
                        ? 'No projects.'
                        : 'No active projects.';
                    return (
                      <p role="status" style={{ color: colors.textSecondary, margin: '0.25rem 0 0' }}>{emptyLine}</p>
                    );
                  }
                  return (
                <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                  {visibleProjects.map(project => {
                  const completedTasks = project.tasks?.filter(t => t.completed).length || 0;
                  const totalTasks = project.tasks?.length || 0;
                  const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
                  const isExpanded = expandedProjects[project.id] === true;
                  const photoCount = projectPhotoCount(project.tasks);
                  const panelId = `project-${project.id}`;

                  return (
                    <div key={project.id} id={`project-card-${project.id}`} style={{
                      background: colors.surface,
                      border: `1px solid ${colors.border}`,
                      borderRadius: isMobile ? '1.25rem' : '0.75rem',
                      padding: isMobile ? '1rem 1.1rem' : '1.15rem 1.25rem',
                    }}>
                      <TapToExpand
                        expanded={isExpanded}
                        onToggle={() => setExpandedProjects(prev => ({...prev, [project.id]: !isExpanded}))}
                        controls={panelId}
                        summary={(
                          <>
                            <span style={{
                              display: 'block',
                              fontSize: isMobile ? '1.05rem' : '1.2rem',
                              fontWeight: 650,
                              lineHeight: 1.35,
                              color: colors.text,
                              overflowWrap: 'anywhere',
                            }}>
                              {project.title}
                            </span>
                            <span style={{
                              display: 'block',
                              marginTop: '0.25rem',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              color: colors.textSecondary,
                            }}>
                              {projectSummaryLine(project.tasks, photoCount)}
                            </span>
                          </>
                        )}
                      >
                      {project.description && (
                        <p style={{margin: '0 0 0.75rem', color: colors.textSecondary}}>
                          {project.description}
                        </p>
                      )}
                      {project.due_date && (
                        <p style={{margin: '0 0 0.75rem', color: '#f59e0b', fontWeight: '600', fontSize: '0.9rem'}}>
                          Due: {formatDateOnly(project.due_date)}
                        </p>
                      )}

                      {/* Progress Bar */}
                      <div style={{marginBottom: '1.5rem'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem'}}>
                          <span style={{fontSize: '0.9rem', fontWeight: '600', color: colors.text}}>
                            Progress: {completedTasks} of {totalTasks} tasks
                          </span>
                          <span style={{fontSize: '0.9rem', fontWeight: '600', color: completionPercentage === 100 ? '#10b981' : brandColor}}>
                            {completionPercentage}%
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '12px',
                          background: colors.surfaceAlt,
                          borderRadius: '6px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${completionPercentage}%`,
                            height: '100%',
                            background: completionPercentage === 100
                              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                              : 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>

                      <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                        {project.tasks?.map((task, index) => {
                          const assignedVendors = taskVendorList(task, projectVendorCatalog());
                          const taskVendors = assignedVendors.length ? [] : matchVendorsToProjectTask(task);
                          const taskKey = `${project.id}-${task.id}`;
                          const isTaskExpanded = expandedProjectTasks[taskKey] === true;

                          return (
                          <div
                            key={task.id}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              padding: '1rem',
                              background: colors.surface,
                              borderRadius: '0.75rem',
                              border: `1px solid ${task.completed ? '#10b981' : colors.borderLight}`,
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {/* Task Header with Checkbox */}
                            <div style={{display: 'flex', gap: '1rem', alignItems: 'flex-start'}}>
                              <input
                                type="checkbox"
                                checked={task.completed || false}
                                disabled={readOnlyHome}
                                onChange={() => handleToggleProjectTask(project.id, task.id)}
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                  marginTop: '2px',
                                  accentColor: '#10b981',
                                  transform: 'scale(1.2)'
                                }}
                              />
                              <div style={{flex: 1}}>
                                <div style={{
                                  fontSize: '1rem',
                                  color: colors.text,
                                  fontWeight: task.completed ? '500' : '600',
                                  textDecoration: task.completed ? 'line-through' : 'none',
                                  opacity: task.completed ? 0.7 : 1
                                }}>
                                  {index + 1}. {task.title}
                                </div>
                                {task.description ? (
                                  <p style={{
                                    margin: '0.3rem 0 0',
                                    fontSize: '0.9rem',
                                    lineHeight: 1.4,
                                    color: colors.textSecondary,
                                    fontWeight: 450,
                                  }}>
                                    {task.description}
                                  </p>
                                ) : null}
                                <ProjectTaskMedia
                                  colors={colors}
                                  brandColor={brandColor}
                                  vendors={assignedVendors}
                                  photos={(task.photos || []).map((photo) => Object.assign({}, photo, {
                                    removable: canWriteProjects && photo.uploaded_by_role === 'homeowner',
                                  }))}
                                  vendorOptions={projectVendorOptions()}
                                  canEditVendors={canWriteProjects}
                                  canAddPhoto={canWriteProjects}
                                  photoBusy={photoBusyKey === `${project.id}:${task.id}`}
                                  onAddVendor={(value) => {
                                    const option = projectVendorOptions().find((item) => item.value === value);
                                    const added = addTaskVendor(task, option && option.vendor, projectVendorCatalog());
                                    changeProjectVendors(project, task, added.task, added.error);
                                  }}
                                  onRemoveVendor={(vendor) => {
                                    changeProjectVendors(project, task, removeTaskVendor(task, vendor.source, vendor.id, projectVendorCatalog()), '');
                                  }}
                                  onPhotoFile={(file, photoError) => addHomeownerTaskPhoto(project, task, file, photoError)}
                                  onRemovePhoto={(photo) => {
                                    if (photo.removable === false) return;
                                    removeHomeownerTaskPhoto(project, task, photo);
                                  }}
                                />
                                {task.notes && (
                                  <div style={{
                                    fontSize: '0.85rem',
                                    color: colors.textSecondary,
                                    marginTop: '0.5rem'
                                  }}>
                                    {task.notes}
                                  </div>
                                )}
                              </div>
                              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0}}>
                                {taskVendors.length > 0 && (
                                  <button
                                    onClick={() => setExpandedProjectTasks(prev => ({
                                      ...prev,
                                      [taskKey]: !isTaskExpanded
                                    }))}
                                    style={{
                                      background: brandColor,
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: isMobile ? '1.25rem' : '0.5rem',
                                      padding: '0.5rem 1rem',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '600',
                                      transition: 'all 0.2s ease',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {isTaskExpanded ? ' Hide' : 'Show'} Vendors ({taskVendors.length})
                                  </button>
                                )}
                                {task.completed && (
                                  <span style={{
                                    color: '#10b981',
                                    fontSize: '1.5rem'
                                  }}>
                                    
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Vendor List - Show when expanded */}
                            {isTaskExpanded && taskVendors.length > 0 && (
                              <div style={{
                                marginTop: '1rem',
                                paddingTop: '1rem',
                                borderTop: `1px solid ${colors.borderLight}`
                              }}>
                                <h4 style={{
                                  margin: '0 0 0.75rem 0',
                                  fontSize: '0.95rem',
                                  color: colors.text,
                                  fontWeight: '600'
                                }}>
                                  Service Providers for this Task
                                </h4>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                                  {taskVendors.map(vendor => (
                                    <div
                                      key={vendor.id}
                                      style={{
                                        padding: '0.75rem',
                                        background: colors.surface,
                                        borderRadius: isMobile ? '1.25rem' : '0.5rem',
                                        border: `1px solid ${colors.borderLight}`
                                      }}
                                    >
                                      <div style={{fontWeight: 600, color: colors.text, fontSize: '0.95rem'}}>
                                        {vendor.name}
                                      </div>
                                      {Array.isArray(vendor.category) ? (
                                        <div style={{
                                          fontSize: '0.8rem',
                                          color: colors.textSecondary,
                                          marginTop: '0.25rem'
                                        }}>
                                          {vendor.category.join(', ')}
                                        </div>
                                      ) : vendor.category && (
                                        <div style={{
                                          fontSize: '0.8rem',
                                          color: colors.textSecondary,
                                          marginTop: '0.25rem'
                                        }}>
                                          {vendor.category}
                                        </div>
                                      )}
                                      <div style={{fontSize: '0.9rem', color: brandColor, marginTop: '0.25rem'}}>
                                        <a href={`tel:${vendor.phone}`} onClick={(event) => gateVendorContact(event, vendor, 'phone', false)} style={{color: brandColor, textDecoration: 'none'}}>
                                          {formatPhoneDisplay(vendor.phone) || vendor.phone}
                                        </a>
                                      </div>
                                      {vendor.email && (
                                        <div style={{fontSize: '0.85rem', color: colors.textSecondary, marginTop: '0.25rem'}}>
                                          <a href={`mailto:${vendor.email}`} onClick={(event) => gateVendorContact(event, vendor, 'email', false)} style={{color: colors.textSecondary, textDecoration: 'none'}}>
                                            {vendor.email}
                                          </a>
                                        </div>
                                      )}
                                      <HomeownerDiscount vendor={vendor} color={isDark ? '#a7f3d0' : '#10b981'} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                        })}
                      </div>
                      </TapToExpand>
                    </div>
                  );
                })}
                </div>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {activeTab === 'vendors' && (
          <div>
            <h2 style={{...styles.sectionTitle, color: colors.text, marginBottom: '0.75rem'}}>Vendors</h2>
            <div style={{ marginBottom: '1.25rem' }}>
              <button
                type="button"
                onClick={() => setProSheet({ pro: null })}
                style={{
                  background: brandColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: '999px',
                  padding: '0.7rem 1.1rem',
                  minHeight: '44px',
                  fontWeight: 650,
                  cursor: 'pointer',
                }}
              >
                {ADD_VENDOR}
              </button>
            </div>
            {(() => {
              const find = vendorTradeFilter
                ? findAProResults({ agentVendors: shownAgentVendors, homeownerPros, trade: vendorTradeFilter })
                : null;
              const tradeMiss = Boolean(find && find.tradeMiss);
              const tradePool = find ? (tradeMiss ? shownAgentVendors : find.agentVendors) : shownAgentVendors;
              const searchLower = vendorSearchTerm.toLowerCase();
              const savedMatches = (find && !tradeMiss ? find.savedPros : []).filter((pro) => {
                if (!vendorSearchTerm) return true;
                return (pro.name || '').toLowerCase().includes(searchLower)
                  || (pro.trade || '').toLowerCase().includes(searchLower);
              });
              const filteredVendors = tradePool.filter((vendor) => {
                const matchesCategory = vendorTradeFilter
                  || selectedVendorCategory === 'All'
                  || vendorMatchesTrade(vendor, selectedVendorCategory);
                const categoryText = [
                  vendor.category,
                  ...(Array.isArray(vendor.categories) ? vendor.categories : []),
                ].filter(Boolean).join(' ');
                const matchesSearch = !vendorSearchTerm
                  || vendor.name?.toLowerCase().includes(searchLower)
                  || stripVendorListingDisclaimer(vendor.description).toLowerCase().includes(searchLower)
                  || categoryText.toLowerCase().includes(searchLower);
                return matchesCategory && matchesSearch;
              });
              const areaSorted = sortVendorsByCounty(
                filteredVendors,
                client.property_county_fips,
                client.state
              );
              const headingStyle = { margin: '0 0 0.25rem', fontSize: '1.15rem', fontWeight: 700, color: colors.text };
              const helpStyle = { margin: '0 0 0.85rem', color: colors.textSecondary, lineHeight: 1.45 };
              const showSponsorTools = shownAgentVendors.length > 0 || Boolean(vendorTradeFilter);

              return (
                <>
                  <p style={{ margin: '0 0 1rem', color: colors.textSecondary, lineHeight: 1.45 }}>{SAVED_VETTING_LINE}</p>
                  {vendorTradeFilter ? (
                    savedMatches.length > 0 ? (
                      <section aria-label={YOUR_VENDORS} style={{ marginBottom: '1.75rem', minWidth: 0 }}>
                        <h3 style={headingStyle}>{YOUR_VENDORS}</h3>
                        <p style={helpStyle}>{YOUR_VENDORS_HELP}</p>
                        <div style={styles.vendorGrid}>
                          {savedMatches.map((pro) => {
                            const expanded = expandedVendorId === pro.id;
                            return (
                              <HomeownerProCard
                                key={pro.id}
                                domId={`vendor-${pro.id}`}
                                pro={pro}
                                colors={colors}
                                accent={brandColor}
                                expanded={expanded}
                                onToggle={() => setExpandedVendorId(expanded ? null : pro.id)}
                                tag={ADDED_BY_YOU}
                              />
                            );
                          })}
                        </div>
                      </section>
                    ) : null
                  ) : (
                    <SavedProvidersSection
                      pros={homeownerPros}
                      colors={colors}
                      accent={brandColor}
                      focusId={vendorFocusId}
                      onEdit={(pro) => setProSheet({ pro })}
                      onDelete={deleteHomeownerPro}
                    />
                  )}
                  {sponsorless ? null : (
                    <section aria-label={sponsorVendorTitle} style={{ marginBottom: '1.75rem', minWidth: 0 }}>
                      <h3 style={headingStyle}>{sponsorVendorTitle}</h3>
                      <p style={helpStyle}>{SPONSOR_VENDORS_HELP}</p>
                      {showSponsorTools ? (
                        <>
                          <div style={{ marginBottom: '1rem' }}>
                            <input
                              type="text"
                              placeholder="Search vendors"
                              value={vendorSearchTerm}
                              onChange={(e) => setVendorSearchTerm(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '0.85rem 1rem',
                                fontSize: '1rem',
                                border: 'none',
                                borderRadius: '1rem',
                                background: colors.surfaceAlt,
                                color: colors.text,
                                boxSizing: 'border-box',
                                boxShadow: vendorSoftShadow(isDark),
                              }}
                            />
                          </div>
                          <VendorCategorySelect
                            id="homeowner-vendor-category"
                            value={vendorTradeFilter || (selectedVendorCategory === 'All' ? '' : selectedVendorCategory)}
                            vendors={shownAgentVendors}
                            colors={colors}
                            onChange={(value) => {
                              setVendorTradeFilter('');
                              setSelectedVendorCategory(value || 'All');
                            }}
                          />
                          {tradeMiss ? (
                            <p style={{ color: colors.text, margin: '0 0 1rem' }}>{noTradeProsNote(vendorTradeFilter)}</p>
                          ) : null}
                        </>
                      ) : null}
                      {areaSorted.length === 0 ? (
                        <p style={{ margin: 0, color: colors.text }}>
                          {shownAgentVendors.length > 0 ? 'No vendors match that search.' : EMPTY_LIST_VENDORS}
                        </p>
                      ) : (
                        <div style={styles.vendorGrid}>
                          {areaSorted.map((vendor) => {
                            const expanded = expandedVendorId === vendor.id;
                            return (
                              <div key={vendor.id} id={`vendor-${vendor.id}`} style={vendorSoftCardStyle(colors, isDark)}>
                                <VendorTapCard
                                  vendor={vendor}
                                  expanded={expanded}
                                  onToggle={() => setExpandedVendorId(expanded ? null : vendor.id)}
                                  accent={brandColor}
                                  contact={vendor.phone ? (
                                    <a
                                      href={`tel:${vendor.phone}`}
                                      aria-label={`Call ${vendor.name}`}
                                      onClick={(event) => gateVendorContact(event, vendor, 'phone', false)}
                                      style={callControlStyle(brandColor, colors.border)}
                                    >
                                      Call
                                    </a>
                                  ) : null}
                                >
                                  <ServesLine
                                    record={vendor}
                                    match={servesCounty(vendor, client.property_county_fips, client.state)}
                                  />
                                  {vendor.phone ? (
                                    <p style={{ margin: '0 0 0.75rem', color: colors.text }}>{formatPhoneDisplay(vendor.phone) || vendor.phone}</p>
                                  ) : null}
                                  {vendor.email && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                      <a
                                        href={`mailto:${vendor.email}`}
                                        onClick={(event) => gateVendorContact(event, vendor, 'email', false)}
                                        style={{ color: colors.textSecondary, fontSize: '0.9rem', textDecoration: 'none', overflowWrap: 'anywhere' }}
                                      >
                                        {vendor.email}
                                      </a>
                                    </div>
                                  )}
                                  {vendor.website && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                      <a
                                        href={vendor.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(event) => gateVendorContact(event, vendor, 'website', true)}
                                        style={{ color: colors.textSecondary, fontSize: '0.9rem', textDecoration: 'none' }}
                                      >
                                        Visit Website
                                      </a>
                                    </div>
                                  )}
                                  <HomeownerDiscount vendor={vendor} color={isDark ? '#ccfbf1' : '#0d9488'} />
                                </VendorTapCard>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  )}
                </>
              );
            })()}
            {proSheet ? (
              <HomeownerProSheet
                pro={proSheet.pro}
                colors={colors}
                customCategories={shownAgentVendors}
                onClose={() => setProSheet(null)}
                onSave={saveHomeownerPro}
              />
            ) : null}
          </div>
        )}

        {activeTab === 'home-history' && (
          <HomeHistoryReportPage colors={colors} isMobile={isMobile} archived={readOnlyHome} native={isNativeApp()} />
        )}

        {activeTab === 'agent' && sponsorless && (
          <PacarionHomeBrand colors={colors} isDark={isDark} isMobile={isMobile} />
        )}

        {activeTab === 'agent' && agent && (
          <div>
            <h2 style={{...styles.sectionTitle, color: colors.text, fontSize: isMobile ? '1.3rem' : '1.5rem'}}>
              Sponsor
            </h2>
            {/* Brokerage Branding */}
            {(agent.logo_url || agent.brokerage_photo_url) && (
              <div style={{
                ...styles.propertyCard,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                {agent.logo_url && (
                  <div style={{marginBottom: agent.brokerage_photo_url ? '1.5rem' : '0'}}>
                    <img
                      src={agent.logo_url}
                      alt="Brokerage Logo"
                      style={{
                        maxWidth: '250px',
                        maxHeight: '100px',
                        objectFit: 'contain',
                        margin: '0 auto'
                      }}
                    />
                  </div>
                )}
                {agent.brokerage_photo_url && (
                  <div>
                    <img
                      src={agent.brokerage_photo_url}
                      alt="Brokerage"
                      style={{
                        width: '100%',
                        maxHeight: '300px',
                        objectFit: 'cover',
                        borderRadius: '0.5rem'
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Agent Information */}
            <div style={{
              ...styles.propertyCard,
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '2rem',
              alignItems: isMobile ? 'center' : 'flex-start',
              textAlign: isMobile ? 'center' : 'left',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box'
            }}>
              {agent.headshot_url && (
                <img
                  src={agent.headshot_url}
                  alt={agent.first_name}
                  style={{
                    width: isMobile ? '120px' : '140px',
                    height: isMobile ? '120px' : '140px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: `4px solid ${brandColor}`,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    flexShrink: 0
                  }}
                />
              )}
              <div style={{flex: 1, width: '100%', minWidth: 0, maxWidth: '100%'}}>
                <h2 style={{
                  margin: '0 0 0.5rem 0',
                  fontSize: isMobile ? '1.5rem' : '1.8rem',
                  color: colors.text,
                  fontWeight: '600',
                  ...WRAP_TEXT
                }}>
                  {[agent.first_name, agent.last_name].filter(Boolean).join(' ') || 'Your sponsor'}
                </h2>
                {(agent.team_name || agent.brokerage) && (
                  <div style={{
                    color: colors.textSecondary,
                    marginBottom: '1.5rem',
                    fontSize: '0.95rem',
                    ...WRAP_TEXT
                  }}>
                    {agent.team_name && (
                      <p style={{ margin: 0, ...WRAP_TEXT }}>{agent.team_name}</p>
                    )}
                    {agent.brokerage && (
                      <p style={{ margin: agent.team_name ? '0.35rem 0 0' : 0, ...WRAP_TEXT }}>{agent.brokerage}</p>
                    )}
                  </div>
                )}

                {/* Contact Buttons */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  marginTop: '1.5rem',
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%'
                }}>
                  {/* Email Button */}
                  <a
                    href={`mailto:${agent.email}`}
                    style={{
                      display: 'block',
                      minWidth: 0,
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      overflowWrap: 'anywhere',
                      padding: '1rem 1.5rem',
                      background: colors.surfaceAlt,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.text,
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = brandColor;
                      e.currentTarget.style.background = colors.surface;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = colors.border;
                      e.currentTarget.style.background = colors.surfaceAlt;
                    }}
                  >
                    <div style={{flex: '1 1 auto', textAlign: 'left', minWidth: 0, maxWidth: '100%'}}>
                      <div style={{
                        fontSize: '0.75rem',
                        color: colors.textSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '0.25rem'
                      }}>
                        Email
                      </div>
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: '500',
                        color: brandColor,
                        ...WRAP_TEXT
                      }}>
                        {agent.email}
                      </div>
                    </div>
                  </a>

                  {/* Phone Button. A missing number or "Not provided" is not a link. */}
                  {phoneTelHref(agent.phone) ? (
                  <a
                    href={phoneTelHref(agent.phone)}
                    style={{
                      display: 'block',
                      minWidth: 0,
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      overflowWrap: 'anywhere',
                      padding: '1rem 1.5rem',
                      background: colors.surfaceAlt,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.text,
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = brandColor;
                      e.currentTarget.style.background = colors.surface;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = colors.border;
                      e.currentTarget.style.background = colors.surfaceAlt;
                    }}
                  >
                    <div style={{flex: '1 1 auto', textAlign: 'left', minWidth: 0, maxWidth: '100%'}}>
                      <div style={{
                        fontSize: '0.75rem',
                        color: colors.textSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '0.25rem'
                      }}>
                        Phone
                      </div>
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: '500',
                        color: brandColor,
                        ...WRAP_TEXT
                      }}>
                        {formatPhoneDisplay(agent.phone)}
                      </div>
                    </div>
                  </a>
                  ) : null}

                  {/* Website Button */}
                  {agent.website && (
                    <a
                      href={agent.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        minWidth: 0,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflowWrap: 'anywhere',
                        padding: '1rem 1.5rem',
                        background: colors.surfaceAlt,
                        border: `2px solid ${colors.border}`,
                        borderRadius: '12px',
                        color: colors.text,
                        textDecoration: 'none',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = brandColor;
                        e.currentTarget.style.background = colors.surface;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.background = colors.surfaceAlt;
                      }}
                    >
                      <div style={{textAlign: 'left', minWidth: 0, maxWidth: '100%'}}>
                        <div style={{
                          fontSize: '0.75rem',
                          color: colors.textSecondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '0.25rem'
                        }}>
                          Website
                        </div>
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: '500',
                          color: brandColor,
                          ...WRAP_TEXT
                        }}>
                          {agent.website.replace(/^https?:\/\//, '')}
                        </div>
                      </div>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    <BeforeYouHireModal
      open={!!hireGate.pending}
      saving={hireGate.saving}
      error={hireGate.error}
      onAccept={hireGate.accept}
      onDismiss={hireGate.dismiss}
      home={client}
    />
    {inventoryEditor && (
      <InventoryItemForm
        colors={colors}
        existingData={inventoryEditor.data}
        initialType={inventoryEditor.systemType}
        features={propertyData.features}
        saving={savingInventory}
        onToggleFeature={handleInventoryFeatureToggle}
        onCancel={() => { if (!savingInventory) setInventoryEditor(null); }}
        onSave={handleSaveInventory}
      />
    )}
    </>
  );
};

const styles = {
  // Container & Layout
  container: {
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif'
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontSize: '1.1rem',
    color: '#59c4c4',
    fontWeight: 400,
    letterSpacing: '0.02em'
  },

  // Header
  header: {
    padding: '2rem 3rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '2rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  },
  headerContent: { display: 'flex', alignItems: 'center', gap: '1.5rem' },
  agentPhoto: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid rgba(255,255,255,0.3)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
  },
  agentPhotoLarge: {
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '5px solid #E5E7EB',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
  },
  headerTitle: {
    margin: 0,
    fontSize: '1.75rem',
    fontWeight: 300,
    letterSpacing: '0.05em'
  },
  headerSubtitle: {
    margin: '0.5rem 0 0 0',
    fontSize: '0.9rem',
    fontWeight: 300,
    letterSpacing: '0.03em',
    textTransform: 'uppercase'
  },
  logoutBtn: {
    background: 'transparent',
    border: '1px solid',
    padding: '0.6rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 400,
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
    letterSpacing: '0.02em'
  },

  // Tabs
  tabContainer: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'visible',
    boxSizing: 'border-box'
  },
  tabs: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    margin: 0,
    padding: '0 1.25rem',
    overflow: 'visible',
    gap: '0'
  },
  tab: {
    flex: '0 0 auto',
    padding: '0.85rem 0.65rem',
    cursor: 'pointer',
    background: 'transparent',
    fontFamily: 'inherit',
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: '2px solid transparent',
    fontWeight: 400,
    fontSize: '0.8125rem',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    letterSpacing: '0.01em',
    textTransform: 'none'
  },
  tabActive: {
    background: 'transparent'
  },

  // Main Content
  main: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '4rem 3rem'
  },

  // Stats Cards
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '2rem',
    marginBottom: '4rem'
  },
  statCard: {
    padding: '3rem 2rem',
    borderRadius: '0',
    textAlign: 'center',
    boxShadow: 'none',
    border: 'none',
    borderLeft: '3px solid',
    transition: 'all 0.2s ease',
    cursor: 'default'
  },
  statNumber: {
    fontSize: '3rem',
    fontWeight: 200,
    letterSpacing: '0.02em',
    lineHeight: 1
  },
  statLabel: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '1rem',
    fontWeight: 400,
    letterSpacing: '0.1em',
    textTransform: 'uppercase'
  },

  // Sections
  section: { marginBottom: '4rem' },
  sectionTitle: {
    fontSize: '1.5rem',
    marginBottom: '2.5rem',
    fontWeight: 300,
    letterSpacing: '0.05em'
  },

  // Alert Box
  alertBox: {
    background: 'linear-gradient(135deg, #F0FAFA 0%, #E0F5F5 100%)',
    border: '2px solid #C5EBEB',
    borderRadius: '16px',
    padding: '2rem',
    marginBottom: '2.5rem',
    boxShadow: '0 4px 16px rgba(89, 196, 196, 0.08)'
  },
  overdueTask: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.25rem',
    padding: '1.25rem',
    background: 'white',
    borderRadius: '12px',
    marginBottom: '0.75rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    transition: 'all 0.2s ease'
  },

  // Task Cards
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  taskCard: {
    padding: '2.5rem',
    borderRadius: '0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    transition: 'all 0.2s ease'
  },
  taskHeader: {
    display: 'flex',
    gap: '1.5rem',
    alignItems: 'flex-start'
  },
  checkbox: {
    width: '22px',
    height: '22px',
    cursor: 'pointer',
    flexShrink: 0,
    marginTop: '0.25rem',
    accentColor: '#59c4c4'
  },
  taskTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1.1rem',
    fontWeight: 400,
    letterSpacing: '0.02em'
  },
  taskMeta: {
    display: 'flex',
    gap: '1.5rem',
    flexWrap: 'wrap',
    fontSize: '0.85rem',
    fontWeight: 300
  },
  badge: {
    padding: '0.4rem 1rem',
    borderRadius: '3px',
    fontSize: '0.75rem',
    fontWeight: 400,
    letterSpacing: '0.05em',
    textTransform: 'uppercase'
  },

  // Buttons
  button: {
    padding: '1rem 1.5rem',
    minHeight: '48px',
    border: 'none',
    borderRadius: '0.75rem',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '1rem',
    marginBottom: '0.75rem',
    width: '100%',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(89, 196, 196, 0.2)',
    letterSpacing: '0.02em',
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    WebkitTapHighlightColor: 'transparent'
  },
  actionBtn: {
    padding: '0.75rem 1.2rem',
    minHeight: '44px',
    border: 'none',
    borderRadius: '0.75rem',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
    color: 'white',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(89, 196, 196, 0.2)',
    letterSpacing: '0.02em',
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    WebkitTapHighlightColor: 'transparent'
  },
  smallBtn: {
    padding: '0.75rem 1.2rem',
    minHeight: '44px',
    border: 'none',
    borderRadius: '0.75rem',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
    color: 'white',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(89, 196, 196, 0.2)',
    letterSpacing: '0.02em',
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    WebkitTapHighlightColor: 'transparent'
  },
  modernBtn: {
    padding: '0.75rem 1.5rem',
    minHeight: '44px',
    border: 'none',
    borderRadius: '0.75rem',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(89, 196, 196, 0.2)',
    letterSpacing: '0.02em',
    WebkitTapHighlightColor: 'transparent'
  },

  // Property & Feature Cards
  propertyCard: {
    padding: '2rem',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 100%), 1fr))',
    gap: '1.25rem',
    minWidth: 0,
    maxWidth: '100%'
  },
  featureCard: {
    padding: '1.75rem',
    borderRadius: '16px',
    textAlign: 'center',
    cursor: 'pointer',
    border: '3px solid',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflowWrap: 'normal',
    wordBreak: 'normal',
    hyphens: 'none'
  },

  // Agent Card
  agentCard: {
    padding: '2.5rem',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    display: 'flex',
    gap: '2.5rem',
    alignItems: 'flex-start',
    marginBottom: '2.5rem'
  },

  // Vendor Cards
  vendorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 17.5rem), 1fr))',
    alignItems: 'start',
    gap: '1rem'
  },
  // Empty State
  emptyState: {
    padding: '3.5rem',
    borderRadius: '16px',
    textAlign: 'center',
    fontSize: '1.15rem',
    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
    fontWeight: 500
  },

  // Forms
  formGroup: { marginBottom: '1.25rem' },
  label: {
    display: 'block',
    fontWeight: 600,
    marginBottom: '0.65rem',
    fontSize: '0.95rem',
    letterSpacing: '-0.01em'
  },
  input: {
    width: '100%',
    padding: '0.9rem 1.1rem',
    borderRadius: '12px',
    fontSize: '1rem',
    boxSizing: 'border-box',
    marginBottom: '0.75rem',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    border: '2px solid #e5e7eb', // Clear default border
    outline: 'none' // Remove default outline
    // Focus state handled via inline styles with onFocus/onBlur or :focus-visible
  },

  // Modal - Fixed for iOS keyboard overlap and safe areas
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.65)',
    display: 'flex',
    alignItems: 'flex-start', // Changed from 'center' to prevent keyboard overlap
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1.5rem',
    paddingTop: 'max(1.5rem, env(safe-area-inset-top))', // Respect notch
    paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))', // Respect home indicator
    paddingLeft: 'max(1.5rem, env(safe-area-inset-left))',
    paddingRight: 'max(1.5rem, env(safe-area-inset-right))',
    backdropFilter: 'blur(8px)',
    overflowY: 'auto' // Allow scrolling when keyboard appears
  },
  modalContent: {
    padding: '2.75rem',
    borderRadius: '20px',
    maxWidth: '540px',
    width: '100%',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    marginTop: '1rem', // Add top margin for better positioning
    marginBottom: '1rem' // Ensure space at bottom when keyboard appears
  }
};

export default ClientDashboardV3;
